/* Source: /home/sidsolaiyappan/skooch_ai/shared/onboarding_reveal.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { revealBuildItems, revealBuildAfter, revealBuildBefore, revealBuildCaps, revealPeakWindows, revealPeakStatLabel, revealPeakStatWord, revealIsSteadyShape } from "./reveal_data.js";
import { getStoreEnergyShape } from "./store.js";
const _jac = {
  int: {
    bit_length(n) {
      if (n === 0) return 0;
      return Math.floor(Math.log2(Math.abs(n))) + 1;
    },
    bit_count(n) {
      let x = Math.abs(n);
      let count = 0;
      while (x) { count += x & 1; x >>= 1; }
      return count;
    },
    to_bytes(n, length, byteorder) {
      const bytes = [];
      let val = n < 0 ? (1 << (length * 8)) + n : n;
      for (let i = 0; i < length; i++) {
        bytes.push(val & 0xff);
        val >>= 8;
      }
      if (byteorder === "big") bytes.reverse();
      return bytes;
    },
    from_bytes(bytes, byteorder) {
      const b = byteorder === "big" ? bytes : [...bytes].reverse();
      let result = 0;
      for (const byte of b) result = (result << 8) | byte;
      return result;
    },
    mod(a, b) { return ((a % b) + b) % b; }
  },

  float: {
    as_integer_ratio(f) {
      if (!isFinite(f)) throw new _jac.exc.ValueError("cannot convert " + f + " to integer ratio");
      if (f === 0) return [0, 1];
      let num = f, den = 1;
      while (num !== Math.floor(num)) { num *= 2; den *= 2; }
      const g = _jac.float._gcd(Math.abs(num), den);
      return [num / g, den / g];
    },
    _gcd(a, b) { while (b) { [a, b] = [b, a % b]; } return a; },
    hex(f) {
      return f.toString(16);
    },
    fromhex(s) {
      return parseFloat(s);
    },
    mod(a, b) { return ((a % b) + b) % b; }
  },

  complex: {
    conjugate(c) { return {re: c.re, im: -c.im}; },
    add(a, b) { return {re: a.re + b.re, im: a.im + b.im}; },
    sub(a, b) { return {re: a.re - b.re, im: a.im - b.im}; },
    mul(a, b) { return {re: a.re * b.re - a.im * b.im, im: a.re * b.im + a.im * b.re}; },
    truediv(a, b) {
      const d = b.re * b.re + b.im * b.im;
      return {re: (a.re * b.re + a.im * b.im) / d, im: (a.im * b.re - a.re * b.im) / d};
    },
    pow(a, b) {
      if (typeof b === "number") b = {re: b, im: 0};
      if (typeof a === "number") a = {re: a, im: 0};
      if (b.im === 0 && Number.isInteger(b.re)) {
        let result = {re: 1, im: 0};
        for (let i = 0; i < Math.abs(b.re); i++) result = _jac.complex.mul(result, a);
        return b.re < 0 ? _jac.complex.truediv({re: 1, im: 0}, result) : result;
      }
      const r = Math.sqrt(a.re * a.re + a.im * a.im);
      const theta = Math.atan2(a.im, a.re);
      const lnr = Math.log(r);
      const newR = Math.exp(lnr * b.re - theta * b.im);
      const newTheta = lnr * b.im + theta * b.re;
      return {re: newR * Math.cos(newTheta), im: newR * Math.sin(newTheta)};
    },
    eq(a, b) {
      if (typeof b === "number") b = {re: b, im: 0};
      if (typeof a === "number") a = {re: a, im: 0};
      return a.re === b.re && a.im === b.im;
    },
    neg(a) { return {re: -a.re, im: -a.im}; },
    pos(a) { return {re: +a.re, im: +a.im}; }
  },

  str: {
    capitalize(s) {
      if (s.length === 0) return s;
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    },
    title(s) {
      return s.replace(/\b\w/g, c => c.toUpperCase());
    },
    swapcase(s) {
      return [...s].map(c => c === c.toUpperCase() ? c.toLowerCase() : c.toUpperCase()).join("");
    },
    count(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      if (sub === "") return t.length + 1;
      let count = 0, pos = 0;
      while ((pos = t.indexOf(sub, pos)) !== -1) { count++; pos += sub.length; }
      return count;
    },
    find(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      const idx = t.indexOf(sub);
      return idx === -1 ? -1 : idx + (start || 0);
    },
    rfind(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      const idx = t.lastIndexOf(sub);
      return idx === -1 ? -1 : idx + (start || 0);
    },
    index(s, sub, start, end) {
      const idx = _jac.str.find(s, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    rindex(s, sub, start, end) {
      const idx = _jac.str.rfind(s, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    startswith(s, prefix, start, end) {
      return s.slice(start || 0, end).startsWith(prefix);
    },
    endswith(s, suffix, start, end) {
      return s.slice(start || 0, end).endsWith(suffix);
    },
    replace(s, old, new_, count) {
      if (count === undefined) return s.split(old).join(new_);
      let result = s, n = 0;
      while (n < count) {
        const idx = result.indexOf(old);
        if (idx === -1) break;
        result = result.slice(0, idx) + new_ + result.slice(idx + old.length);
        n++;
      }
      return result;
    },
    strip(s, chars) {
      if (!chars) return s.trim();
      let start = 0, end = s.length;
      while (start < end && chars.indexOf(s[start]) !== -1) start++;
      while (end > start && chars.indexOf(s[end - 1]) !== -1) end--;
      return s.slice(start, end);
    },
    lstrip(s, chars) {
      if (!chars) return s.trimStart();
      let start = 0;
      while (start < s.length && chars.indexOf(s[start]) !== -1) start++;
      return s.slice(start);
    },
    rstrip(s, chars) {
      if (!chars) return s.trimEnd();
      let end = s.length;
      while (end > 0 && chars.indexOf(s[end - 1]) !== -1) end--;
      return s.slice(0, end);
    },
    removeprefix(s, prefix) {
      return s.startsWith(prefix) ? s.slice(prefix.length) : s;
    },
    removesuffix(s, suffix) {
      return (suffix && s.endsWith(suffix)) ? s.slice(0, -suffix.length) : s;
    },
    split(s, sep, maxsplit) {
      if (sep === undefined || sep === null) {
        const parts = s.trim().split(/\s+/);
        if (maxsplit === undefined) return parts;
        if (maxsplit <= 0) return [s];
        return [...parts.slice(0, maxsplit), parts.slice(maxsplit).join(" ")].filter(x => x !== undefined);
      }
      if (maxsplit === undefined) return s.split(sep);
      const parts = s.split(sep);
      if (parts.length <= maxsplit + 1) return parts;
      return [...parts.slice(0, maxsplit), parts.slice(maxsplit).join(sep)];
    },
    rsplit(s, sep, maxsplit) {
      if (maxsplit === undefined) return _jac.str.split(s, sep);
      const parts = s.split(sep);
      if (parts.length <= maxsplit + 1) return parts;
      const keep = parts.length - maxsplit;
      return [parts.slice(0, keep).join(sep), ...parts.slice(keep)];
    },
    splitlines(s, keepends) {
      const lines = s.split(/(\r\n|\r|\n)/);
      const result = [];
      for (let i = 0; i < lines.length; i += 2) {
        const line = lines[i];
        const sep = lines[i + 1] || "";
        if (line || sep) result.push(keepends ? line + sep : line);
      }
      return result;
    },
    partition(s, sep) {
      const idx = s.indexOf(sep);
      if (idx === -1) return [s, "", ""];
      return [s.slice(0, idx), sep, s.slice(idx + sep.length)];
    },
    rpartition(s, sep) {
      const idx = s.lastIndexOf(sep);
      if (idx === -1) return ["", "", s];
      return [s.slice(0, idx), sep, s.slice(idx + sep.length)];
    },
    format(s, ...args) {
      let i = 0;
      return s.replace(/\{(\w*)\}/g, (match, key) => {
        if (key === "") return args[i++];
        if (!isNaN(key)) return args[parseInt(key)];
        return args[0] && typeof args[0] === "object" ? args[0][key] : match;
      });
    },
    format_map(s, mapping) {
      return s.replace(/\{(\w+)\}/g, (match, key) => key in mapping ? mapping[key] : match);
    },
    center(s, width, fillchar) {
      const fc = fillchar || " ";
      if (s.length >= width) return s;
      const total = width - s.length;
      const left = Math.floor(total / 2);
      return fc.repeat(left) + s + fc.repeat(total - left);
    },
    zfill(s, width) {
      if (s.length >= width) return s;
      const sign = (s[0] === "+" || s[0] === "-") ? s[0] : "";
      const body = sign ? s.slice(1) : s;
      return sign + "0".repeat(width - s.length) + body;
    },
    expandtabs(s, tabsize) {
      const ts = tabsize === undefined ? 8 : tabsize;
      let result = "", col = 0;
      for (const c of s) {
        if (c === "\t") { const sp = ts - (col % ts); result += " ".repeat(sp); col += sp; }
        else if (c === "\n" || c === "\r") { result += c; col = 0; }
        else { result += c; col++; }
      }
      return result;
    },
    isnumeric(s) {
      if (s.length === 0) return false;
      return [...s].every(c => /[\d\u00B2\u00B3\u00B9\u00BC-\u00BE\u2150-\u218F]/.test(c));
    },
    istitle(s) {
      return s === _jac.str.title(s) && /[A-Za-z]/.test(s);
    },
    translate(s, table) {
      return [...s].map(c => {
        const code = c.charCodeAt(0);
        if (code in table) {
          const v = table[code];
          if (v === null) return "";
          if (typeof v === "number") return String.fromCharCode(v);
          return v;
        }
        return c;
      }).join("");
    },
    maketrans(...args) {
      const table = {};
      if (args.length === 1) {
        const mapping = args[0];
        for (const [k, v] of Object.entries(mapping)) {
          table[typeof k === "string" ? k.charCodeAt(0) : k] = v;
        }
      } else if (args.length >= 2) {
        const from_ = args[0], to_ = args[1];
        for (let i = 0; i < from_.length; i++) {
          table[from_.charCodeAt(i)] = to_.charCodeAt(i);
        }
        if (args[2]) {
          for (const c of args[2]) table[c.charCodeAt(0)] = null;
        }
      }
      return table;
    },
    mod(s, args) {
      let i = 0;
      const a = Array.isArray(args) ? args : [args];
      return s.replace(/%([sd%])/g, (m, fmt) => {
        if (fmt === "%") return "%";
        return String(a[i++]);
      });
    }
  },

  // Runtime-polymorphic method dispatch, used when the codegen cannot prove
  // the static type of a method-call receiver (e.g. a `dict`-typed prop, the
  // result of `or` widening, or a comprehension over an untyped value) and so
  // cannot pick a typed emitter. The compiler emits
  // `_jac.poly.call(obj, "name", ...args)`; `call` resolves the *runtime* type
  // of `obj` and applies the matching Python operation, forwards to a
  // same-named method on a user-defined object, or raises AttributeError. The
  // per-type tables below carry the trivial native-mapped methods inline and
  // delegate the non-trivial ones to the shared `_jac.str/list/dict/set`
  // helpers, so each operation has a single implementation and no Python
  // method name can leak verbatim into the emitted JavaScript.
  poly: {
    _typename(o) {
      if (o === null || o === undefined) return "NoneType";
      if (Array.isArray(o)) return "list";
      if (o instanceof Set) return "set";
      if (o instanceof Map) return "dict";
      if (o instanceof Uint8Array) return "bytes";
      const t = typeof o;
      if (t === "string") return "str";
      if (t === "boolean") return "bool";
      if (t === "number") return Number.isInteger(o) ? "int" : "float";
      if (t === "object") return (o.constructor && o.constructor.name) || "object";
      return t;
    },
    // --- per-runtime-type Python method tables (keyed by Python name) ---
    // Trivial native-mapped ops are inline; non-trivial ones delegate to the
    // shared `_jac.<type>` helpers (which the typed emitters also use), so the
    // typed and polymorphic paths share one implementation.
    _str: {
      lower: (s) => s.toLowerCase(),
      upper: (s) => s.toUpperCase(),
      casefold: (s) => s.toLowerCase(),
      strip: (s, chars) => {
        if (chars === undefined) return s.trim();
        let i = 0, j = s.length;
        while (i < j && chars.indexOf(s[i]) !== -1) i++;
        while (j > i && chars.indexOf(s[j - 1]) !== -1) j--;
        return s.slice(i, j);
      },
      lstrip: (s, chars) => {
        if (chars === undefined) return s.replace(/^\s+/, "");
        let i = 0;
        while (i < s.length && chars.indexOf(s[i]) !== -1) i++;
        return s.slice(i);
      },
      rstrip: (s, chars) => {
        if (chars === undefined) return s.replace(/\s+$/, "");
        let j = s.length;
        while (j > 0 && chars.indexOf(s[j - 1]) !== -1) j--;
        return s.slice(0, j);
      }
    },
    _list: {
      append: (a, x) => { a.push(x); return null; },
      clear: (a) => { a.length = 0; return null; },
      copy: (a) => a.slice(),
      count: (a, x) => _jac.list.count(a, x),
      extend: (a, b) => { for (const v of b) a.push(v); return null; },
      index: (a, x, start, end) => _jac.list.index(a, x, start, end),
      insert: (a, i, x) => { a.splice(i, 0, x); return null; },
      pop: (a, i) => {
        if (i === undefined) return a.pop();
        const idx = i < 0 ? a.length + i : i;
        return a.splice(idx, 1)[0];
      },
      remove: (a, x) => _jac.list.remove(a, x),
      reverse: (a) => { a.reverse(); return null; },
      sort: (a, key) => _jac.list.sort(a, key)
    },
    _dict: {
      clear: (o) => _jac.dict.clear(o),
      copy: (o) => ({ ...o }),
      // Python `dict.get`: returns the stored value when the key is present
      // (even if it is null), and the default only when the key is absent.
      // A membership check is required — `o[key] ?? default_` would wrongly
      // substitute the default for a present-but-null value.
      get: (o, key, default_) =>
        Object.prototype.hasOwnProperty.call(o, key) ? o[key] : default_,
      items: (o) => Object.entries(o),
      keys: (o) => Object.keys(o),
      pop: (o, key, default_) => _jac.dict.pop(o, key, default_),
      popitem: (o) => _jac.dict.popitem(o),
      setdefault: (o, key, default_) => _jac.dict.setdefault(o, key, default_),
      update: (o, other) => {
        if (other instanceof Map) { for (const [k, v] of other) o[k] = v; }
        else if (Array.isArray(other)) { for (const [k, v] of other) o[k] = v; }
        else if (other) { for (const k of Object.keys(other)) o[k] = other[k]; }
        return null;
      },
      values: (o) => Object.values(o)
    },
    _set: {
      add: (s, x) => { s.add(x); return null; },
      clear: (s) => { s.clear(); return null; },
      copy: (s) => new Set(s),
      difference: (a, b) => _jac.set.difference(a, b),
      difference_update: (s, o) => _jac.set.difference_update(s, o),
      discard: (s, x) => { s.delete(x); return null; },
      intersection: (a, b) => _jac.set.intersection(a, b),
      intersection_update: (s, o) => _jac.set.intersection_update(s, o),
      isdisjoint: (a, b) => _jac.set.isDisjointFrom(a, b instanceof Set ? b : new Set(b)),
      issubset: (a, b) => _jac.set.isSubsetOf(a, b instanceof Set ? b : new Set(b)),
      issuperset: (a, b) => _jac.set.isSupersetOf(a, b instanceof Set ? b : new Set(b)),
      pop: (s) => _jac.set.pop(s),
      remove: (s, x) => _jac.set.remove(s, x),
      symmetric_difference: (a, b) => _jac.set.symmetricDifference(a, b),
      symmetric_difference_update: (s, o) => _jac.set.symmetric_difference_update(s, o),
      union: (a, b) => _jac.set.union(a, b),
      update: (s, o) => _jac.set.update(s, o)
    },
    // Jac dicts compile to plain objects, but interop code may hand us a real
    // Map; mirror the dict methods over the Map API for that case.
    _map: {
      clear: (m) => { m.clear(); return null; },
      copy: (m) => new Map(m),
      get: (m, key, default_) => (m.has(key) ? m.get(key) : default_),
      items: (m) => [...m.entries()],
      keys: (m) => [...m.keys()],
      pop: (m, key, default_) => {
        if (m.has(key)) { const v = m.get(key); m.delete(key); return v; }
        if (default_ !== undefined) return default_;
        throw new _jac.exc.KeyError("KeyError: " + key);
      },
      popitem: (m) => {
        if (m.size === 0) throw new _jac.exc.KeyError("dictionary is empty");
        const key = [...m.keys()].pop();
        const val = m.get(key);
        m.delete(key);
        return [key, val];
      },
      setdefault: (m, key, default_) => {
        if (!m.has(key)) m.set(key, default_ === undefined ? null : default_);
        return m.get(key);
      },
      update: (m, other) => {
        if (other instanceof Map) { for (const [k, v] of other) m.set(k, v); }
        else if (Array.isArray(other)) { for (const [k, v] of other) m.set(k, v); }
        else if (other) { for (const k of Object.keys(other)) m.set(k, other[k]); }
        return null;
      },
      values: (m) => [...m.values()]
    },
    // Single entry point. `obj.method(...args)` on a receiver whose static
    // type is not provably primitive is compiled to
    // `_jac.poly.call(obj, "method", ...args)`.
    call(o, m, ...args) {
      // 1. Genuine JS primitives get Python-semantic dispatch: a plain object
      //    is a Python dict, an Array is a list, a Set/Map/string/number/bytes
      //    maps to its Python counterpart. These are the types whose Python
      //    method semantics differ from (or have no) native JS equivalent.
      if (typeof o === "string") {
        if (_jac.poly._str[m]) return _jac.poly._str[m](o, ...args);
        if (_jac.str[m]) return _jac.str[m](o, ...args);
      } else if (typeof o === "number") {
        const ns = Number.isInteger(o) ? _jac.int : _jac.float;
        if (ns && ns[m]) return ns[m](o, ...args);
      } else if (Array.isArray(o)) {
        if (_jac.poly._list[m]) return _jac.poly._list[m](o, ...args);
      } else if (o instanceof Set) {
        if (_jac.poly._set[m]) return _jac.poly._set[m](o, ...args);
      } else if (o instanceof Map) {
        if (_jac.poly._map[m]) return _jac.poly._map[m](o, ...args);
      } else if (o instanceof Uint8Array) {
        if (_jac.bytes[m]) return _jac.bytes[m](o, ...args);
      } else if (
        o !== null && o !== undefined && typeof o === "object"
        && (Object.getPrototypeOf(o) === Object.prototype
            || Object.getPrototypeOf(o) === null)
      ) {
        // A plain object is a Python dict literal.
        if (_jac.poly._dict[m]) return _jac.poly._dict[m](o, ...args);
      }
      // 2. Otherwise forward to a same-named method the receiver carries
      //    itself: a user-defined class instance, a host/framework object
      //    (Headers, URLSearchParams, ...), a callable with attached methods,
      //    or a Set/Map/Array method not mirrored above. This matches the
      //    direct `obj.method(...)` that a typed call would have emitted.
      if (o !== null && o !== undefined && typeof o[m] === "function") {
        return o[m](...args);
      }
      // 3. Nothing applies: raise the Python-style error instead of leaking a
      //    method name or silently returning a wrong value.
      throw new _jac.exc.AttributeError(
        "'" + _jac.poly._typename(o) + "' object has no attribute '" + m + "'");
    },
    // Backward-compatible named shims. The compiler now emits `call`, but a
    // previously-built bundle may still reference a named helper; these route
    // it through the same dispatcher (rest params preserve the arg count).
    lower(s, ...r) { return _jac.poly.call(s, "lower", ...r); },
    upper(s, ...r) { return _jac.poly.call(s, "upper", ...r); },
    strip(s, ...r) { return _jac.poly.call(s, "strip", ...r); },
    lstrip(s, ...r) { return _jac.poly.call(s, "lstrip", ...r); },
    rstrip(s, ...r) { return _jac.poly.call(s, "rstrip", ...r); },
    append(a, ...r) { return _jac.poly.call(a, "append", ...r); },
    extend(a, ...r) { return _jac.poly.call(a, "extend", ...r); },
    pop(a, ...r) { return _jac.poly.call(a, "pop", ...r); },
    get(o, ...r) { return _jac.poly.call(o, "get", ...r); },
    contains(c, x) {
      // Python-equivalent membership for a value of unknown static type.
      // string -> substring; array (list/tuple/range) -> element;
      // Set/Map -> key/element; plain object (dict) -> own key.
      if (typeof c === "string") return c.includes(x);
      if (Array.isArray(c)) return c.includes(x);
      if (c instanceof Set || c instanceof Map) return c.has(x);
      if (c !== null && typeof c === "object") {
        return Object.prototype.hasOwnProperty.call(c, x);
      }
      return false;
    },
    getitem(c, i) {
      // Subscript read for a value of unknown static type. This is a faithful
      // superset of a raw `c[i]`: it only adds Python's negative indexing for
      // sequences (string/array) and otherwise preserves JS semantics exactly
      // (mapping/object access, out-of-range -> `undefined`, no new throws).
      if (typeof i === "number" && i < 0
          && (typeof c === "string" || Array.isArray(c))) {
        return c[c.length + i];
      }
      if (c instanceof Map) return c.get(i);
      return c[i];
    },
    setitem(c, i, v) {
      // Subscript assignment for a value of unknown/list static type. A
      // negative index writes the array element counted from the end (a raw
      // JS `c[-1] = v` would instead create a bogus "-1" property); every
      // other case behaves like a plain `c[i] = v`.
      if (typeof i === "number" && i < 0 && Array.isArray(c)) {
        c[c.length + i] = v;
        return v;
      }
      if (c instanceof Map) { c.set(i, v); return v; }
      c[i] = v;
      return v;
    },
    and_(a, bf) {
      // Python `a and b`: returns `a` when it is falsy, else evaluates and
      // returns `b`. `bf` is a thunk so the right operand keeps short-circuit
      // (lazy) evaluation, and `a` is evaluated exactly once by the caller.
      return _jac.builtin.bool(a) ? bf() : a;
    },
    or_(a, bf) {
      // Python `a or b`: returns `a` when it is truthy, else `b`.
      return _jac.builtin.bool(a) ? a : bf();
    }
  },

  list: {
    remove(arr, val) {
      const i = arr.indexOf(val);
      if (i === -1) throw new _jac.exc.ValueError("list.remove(x): x not in list");
      arr.splice(i, 1);
    },
    index(arr, val, start, end) {
      const s = start || 0;
      const e = end === undefined ? arr.length : end;
      for (let i = s; i < e; i++) {
        if (arr[i] === val) return i;
      }
      throw new _jac.exc.ValueError(val + " is not in list");
    },
    count(arr, val) {
      let c = 0;
      for (const item of arr) { if (item === val) c++; }
      return c;
    },
    sort(arr, key) {
      if (key) {
        arr.sort((a, b) => {
          const ka = key(a), kb = key(b);
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      } else {
        arr.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
      }
    },
    repeat(arr, n) {
      const result = [];
      for (let i = 0; i < n; i++) result.push(...arr);
      return result;
    },
    eq(a, b) { return JSON.stringify(a) === JSON.stringify(b); },
    lt(a, b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return true;
        if (a[i] > b[i]) return false;
      }
      return a.length < b.length;
    },
    gt(a, b) { return _jac.list.lt(b, a); },
    le(a, b) { return _jac.list.eq(a, b) || _jac.list.lt(a, b); },
    ge(a, b) { return _jac.list.eq(a, b) || _jac.list.gt(a, b); },
    imul(arr, n) {
      const orig = [...arr];
      arr.length = 0;
      for (let i = 0; i < n; i++) arr.push(...orig);
      return arr;
    }
  },

  dict: {
    pop(d, key, default_) {
      if (key in d) { const v = d[key]; delete d[key]; return v; }
      if (default_ !== undefined) return default_;
      throw new _jac.exc.KeyError("KeyError: " + key);
    },
    popitem(d) {
      const keys = Object.keys(d);
      if (keys.length === 0) throw new _jac.exc.KeyError("dictionary is empty");
      const key = keys[keys.length - 1];
      const val = d[key];
      delete d[key];
      return [key, val];
    },
    setdefault(d, key, default_) {
      if (!(key in d)) d[key] = default_ === undefined ? null : default_;
      return d[key];
    },
    clear(d) {
      for (const key of Object.keys(d)) delete d[key];
    },
    fromkeys(keys, value) {
      const d = {};
      const v = value === undefined ? null : value;
      for (const k of keys) d[k] = v;
      return d;
    },
    eq(a, b) {
      const ka = Object.keys(a), kb = Object.keys(b);
      if (ka.length !== kb.length) return false;
      for (const k of ka) {
        if (!(k in b)) return false;
        const va = a[k], vb = b[k];
        if (va !== vb) {
          if (typeof va === "object" && typeof vb === "object" && va !== null && vb !== null) {
            if (JSON.stringify(va) !== JSON.stringify(vb)) return false;
          } else return false;
        }
      }
      return true;
    }
  },

  set: {
    remove(s, val) {
      if (!s.has(val)) throw new _jac.exc.KeyError("KeyError: " + val);
      s.delete(val);
    },
    pop(s) {
      if (s.size === 0) throw new _jac.exc.KeyError("pop from an empty set");
      const val = s.values().next().value;
      s.delete(val);
      return val;
    },
    update(s, other) { for (const v of other) s.add(v); },
    intersection_update(s, other) {
      const otherSet = other instanceof Set ? other : new Set(other);
      for (const v of s) { if (!otherSet.has(v)) s.delete(v); }
    },
    difference_update(s, other) {
      for (const v of other) s.delete(v);
    },
    symmetric_difference_update(s, other) {
      for (const v of other) { if (s.has(v)) s.delete(v); else s.add(v); }
    },
    union(a, b) {
      const r = new Set(a);
      for (const v of b) r.add(v);
      return r;
    },
    intersection(a, b) {
      const bs = b instanceof Set ? b : new Set(b);
      const r = new Set();
      for (const v of a) { if (bs.has(v)) r.add(v); }
      return r;
    },
    difference(a, b) {
      const bs = b instanceof Set ? b : new Set(b);
      const r = new Set();
      for (const v of a) { if (!bs.has(v)) r.add(v); }
      return r;
    },
    symmetricDifference(a, b) {
      const r = new Set(a);
      for (const v of b) { if (r.has(v)) r.delete(v); else r.add(v); }
      return r;
    },
    isSubsetOf(a, b) {
      if (a.size > b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    },
    isSupersetOf(a, b) { return _jac.set.isSubsetOf(b, a); },
    isDisjointFrom(a, b) {
      for (const v of a) { if (b.has(v)) return false; }
      return true;
    },
    is_proper_subset(a, b) {
      if (a.size >= b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    },
    is_proper_superset(a, b) { return _jac.set.is_proper_subset(b, a); },
    eq(a, b) {
      if (a.size !== b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    }
  },

  bytes: {
    hex(b) {
      return Array.from(b).map(x => x.toString(16).padStart(2, "0")).join("");
    },
    fromhex(s) {
      const arr = [];
      const clean = s.replace(/\s/g, "");
      for (let i = 0; i < clean.length; i += 2) arr.push(parseInt(clean.substr(i, 2), 16));
      return new Uint8Array(arr);
    },
    _indexOf(b, sub, start, end) {
      const s = start || 0;
      const e = end === undefined ? b.length : end;
      const sl = sub.length;
      if (sl === 0) return s;
      for (let i = s; i <= e - sl; i++) {
        let m = true;
        for (let j = 0; j < sl; j++) { if (b[i + j] !== sub[j]) { m = false; break; } }
        if (m) return i;
      }
      return -1;
    },
    _lastIndexOf(b, sub, start, end) {
      const s = start || 0;
      const e = end === undefined ? b.length : end;
      const sl = sub.length;
      if (sl === 0) return e;
      for (let i = e - sl; i >= s; i--) {
        let m = true;
        for (let j = 0; j < sl; j++) { if (b[i + j] !== sub[j]) { m = false; break; } }
        if (m) return i;
      }
      return -1;
    },
    count(b, sub) {
      let c = 0, p = 0;
      while (p <= b.length - sub.length) {
        const idx = _jac.bytes._indexOf(b, sub, p);
        if (idx === -1) break;
        c++; p = idx + sub.length;
      }
      return c;
    },
    find(b, sub, start, end) { return _jac.bytes._indexOf(b, sub, start, end); },
    rfind(b, sub, start, end) { return _jac.bytes._lastIndexOf(b, sub, start, end); },
    index(b, sub, start, end) {
      const idx = _jac.bytes._indexOf(b, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    rindex(b, sub, start, end) {
      const idx = _jac.bytes._lastIndexOf(b, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    startswith(b, prefix, start, end) {
      const s = start || 0;
      if (prefix.length > b.length - s) return false;
      for (let i = 0; i < prefix.length; i++) { if (b[s + i] !== prefix[i]) return false; }
      return true;
    },
    endswith(b, suffix, start, end) {
      const e = end === undefined ? b.length : end;
      if (suffix.length > e) return false;
      const off = e - suffix.length;
      for (let i = 0; i < suffix.length; i++) { if (b[off + i] !== suffix[i]) return false; }
      return true;
    },
    replace(b, old_, new_, count) {
      const r = [];
      let pos = 0, n = 0;
      const max = count === undefined ? Infinity : count;
      while (pos <= b.length) {
        if (n < max) {
          const idx = _jac.bytes._indexOf(b, old_, pos);
          if (idx !== -1) {
            for (let i = pos; i < idx; i++) r.push(b[i]);
            for (let i = 0; i < new_.length; i++) r.push(new_[i]);
            pos = idx + old_.length; n++;
            continue;
          }
        }
        for (let i = pos; i < b.length; i++) r.push(b[i]);
        break;
      }
      return new Uint8Array(r);
    },
    _isWS(c) { return c === 32 || c === 9 || c === 10 || c === 13 || c === 11 || c === 12; },
    strip(b, chars) {
      let s = 0, e = b.length;
      if (chars) { const cs = new Set(chars); while (s < e && cs.has(b[s])) s++; while (e > s && cs.has(b[e-1])) e--; }
      else { while (s < e && _jac.bytes._isWS(b[s])) s++; while (e > s && _jac.bytes._isWS(b[e-1])) e--; }
      return b.slice(s, e);
    },
    lstrip(b, chars) {
      let s = 0;
      if (chars) { const cs = new Set(chars); while (s < b.length && cs.has(b[s])) s++; }
      else { while (s < b.length && _jac.bytes._isWS(b[s])) s++; }
      return b.slice(s);
    },
    rstrip(b, chars) {
      let e = b.length;
      if (chars) { const cs = new Set(chars); while (e > 0 && cs.has(b[e-1])) e--; }
      else { while (e > 0 && _jac.bytes._isWS(b[e-1])) e--; }
      return b.slice(0, e);
    },
    removeprefix(b, prefix) {
      if (_jac.bytes.startswith(b, prefix)) return b.slice(prefix.length);
      return b.slice();
    },
    removesuffix(b, suffix) {
      if (suffix.length > 0 && _jac.bytes.endswith(b, suffix)) return b.slice(0, b.length - suffix.length);
      return b.slice();
    },
    split(b, sep, maxsplit) {
      const parts = [];
      let pos = 0, n = 0;
      const max = maxsplit === undefined ? Infinity : maxsplit;
      while (pos <= b.length) {
        if (n >= max) { parts.push(b.slice(pos)); return parts; }
        const idx = _jac.bytes._indexOf(b, sep, pos);
        if (idx === -1) { parts.push(b.slice(pos)); return parts; }
        parts.push(b.slice(pos, idx));
        pos = idx + sep.length; n++;
      }
      if (pos <= b.length) parts.push(b.slice(pos));
      return parts;
    },
    rsplit(b, sep, maxsplit) {
      if (maxsplit === undefined) return _jac.bytes.split(b, sep);
      const parts = [];
      let end = b.length, n = 0;
      while (end >= 0 && n < maxsplit) {
        const idx = _jac.bytes._lastIndexOf(b, sep, 0, end);
        if (idx === -1) break;
        parts.unshift(b.slice(idx + sep.length, end));
        end = idx; n++;
      }
      parts.unshift(b.slice(0, end));
      return parts;
    },
    splitlines(b, keepends) {
      const result = [];
      let start = 0;
      for (let i = 0; i < b.length; i++) {
        if (b[i] === 10 || b[i] === 13) {
          let eol = i + 1;
          if (b[i] === 13 && i + 1 < b.length && b[i + 1] === 10) eol++;
          result.push(keepends ? b.slice(start, eol) : b.slice(start, i));
          start = eol;
          if (eol > i + 1) i++;
        }
      }
      if (start < b.length) result.push(b.slice(start));
      return result;
    },
    join(sep, parts) {
      if (parts.length === 0) return new Uint8Array(0);
      const r = [];
      for (let i = 0; i < parts.length; i++) {
        if (i > 0) for (const x of sep) r.push(x);
        for (const x of parts[i]) r.push(x);
      }
      return new Uint8Array(r);
    },
    partition(b, sep) {
      const idx = _jac.bytes._indexOf(b, sep);
      if (idx === -1) return [b.slice(), new Uint8Array(0), new Uint8Array(0)];
      return [b.slice(0, idx), sep.slice(), b.slice(idx + sep.length)];
    },
    rpartition(b, sep) {
      const idx = _jac.bytes._lastIndexOf(b, sep);
      if (idx === -1) return [new Uint8Array(0), new Uint8Array(0), b.slice()];
      return [b.slice(0, idx), sep.slice(), b.slice(idx + sep.length)];
    },
    _isUp(c) { return c >= 65 && c <= 90; },
    _isLo(c) { return c >= 97 && c <= 122; },
    _isAl(c) { return (c >= 65 && c <= 90) || (c >= 97 && c <= 122); },
    _isDig(c) { return c >= 48 && c <= 57; },
    capitalize(b) {
      if (b.length === 0) return new Uint8Array(0);
      const r = new Uint8Array(b.length);
      r[0] = _jac.bytes._isLo(b[0]) ? b[0] - 32 : b[0];
      for (let i = 1; i < b.length; i++) r[i] = _jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i];
      return r;
    },
    lower(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) r[i] = _jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i];
      return r;
    },
    upper(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) r[i] = _jac.bytes._isLo(b[i]) ? b[i] - 32 : b[i];
      return r;
    },
    title(b) {
      const r = new Uint8Array(b.length);
      let prev = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isAl(b[i])) {
          r[i] = prev ? (_jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i]) : (_jac.bytes._isLo(b[i]) ? b[i] - 32 : b[i]);
          prev = true;
        } else { r[i] = b[i]; prev = false; }
      }
      return r;
    },
    swapcase(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) r[i] = b[i] + 32;
        else if (_jac.bytes._isLo(b[i])) r[i] = b[i] - 32;
        else r[i] = b[i];
      }
      return r;
    },
    center(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const total = width - b.length;
      const left = Math.floor(total / 2);
      const r = new Uint8Array(width);
      r.fill(f, 0, left); r.set(b, left); r.fill(f, left + b.length);
      return r;
    },
    ljust(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      r.set(b); r.fill(f, b.length);
      return r;
    },
    rjust(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      const off = width - b.length;
      r.fill(f, 0, off); r.set(b, off);
      return r;
    },
    zfill(b, width) {
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      let off = 0;
      if (b.length > 0 && (b[0] === 43 || b[0] === 45)) { r[0] = b[0]; off = 1; }
      const pad = width - b.length;
      r.fill(48, off, off + pad);
      for (let i = off; i < b.length; i++) r[pad + i] = b[i];
      return r;
    },
    expandtabs(b, tabsize) {
      const ts = tabsize === undefined ? 8 : tabsize;
      const r = [];
      let col = 0;
      for (let i = 0; i < b.length; i++) {
        if (b[i] === 9) { const sp = ts - (col % ts); for (let j = 0; j < sp; j++) r.push(32); col += sp; }
        else if (b[i] === 10 || b[i] === 13) { r.push(b[i]); col = 0; }
        else { r.push(b[i]); col++; }
      }
      return new Uint8Array(r);
    },
    translate(b, table, del_) {
      const r = [];
      const ds = del_ ? new Set(del_) : null;
      for (let i = 0; i < b.length; i++) {
        if (ds && ds.has(b[i])) continue;
        r.push(table && table[b[i]] !== undefined ? table[b[i]] : b[i]);
      }
      return new Uint8Array(r);
    },
    maketrans(from_, to_) {
      const t = new Uint8Array(256);
      for (let i = 0; i < 256; i++) t[i] = i;
      for (let i = 0; i < from_.length; i++) t[from_[i]] = to_[i];
      return t;
    },
    isalnum(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isAl(b[i]) && !_jac.bytes._isDig(b[i])) return false;
      return true;
    },
    isalpha(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isAl(b[i])) return false;
      return true;
    },
    isascii(b) {
      for (let i = 0; i < b.length; i++) if (b[i] > 127) return false;
      return true;
    },
    isdigit(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isDig(b[i])) return false;
      return true;
    },
    islower(b) {
      let has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) return false;
        if (_jac.bytes._isLo(b[i])) has = true;
      }
      return has;
    },
    isupper(b) {
      let has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isLo(b[i])) return false;
        if (_jac.bytes._isUp(b[i])) has = true;
      }
      return has;
    },
    isspace(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isWS(b[i])) return false;
      return true;
    },
    istitle(b) {
      let prev = false, has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) { if (prev) return false; prev = true; has = true; }
        else if (_jac.bytes._isLo(b[i])) { if (!prev) return false; prev = true; has = true; }
        else prev = false;
      }
      return has;
    },
    add(a, b) {
      const r = new Uint8Array(a.length + b.length);
      r.set(a); r.set(b, a.length);
      return r;
    },
    mul(b, n) {
      const r = new Uint8Array(b.length * n);
      for (let i = 0; i < n; i++) r.set(b, i * b.length);
      return r;
    },
    mod(b, args) {
      const s = new TextDecoder().decode(b);
      return new TextEncoder().encode(_jac.str.mod(s, args));
    },
    eq(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
      return true;
    },
    lt(a, b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return true;
        if (a[i] > b[i]) return false;
      }
      return a.length < b.length;
    },
    gt(a, b) { return _jac.bytes.lt(b, a); },
    le(a, b) { return _jac.bytes.eq(a, b) || _jac.bytes.lt(a, b); },
    ge(a, b) { return _jac.bytes.eq(a, b) || _jac.bytes.gt(a, b); },
    contains(b, sub) { return _jac.bytes._indexOf(b, sub) !== -1; }
  },

  tuple: {
    count(arr, val) { return _jac.list.count(arr, val); },
    index(arr, val, start, end) { return _jac.list.index(arr, val, start, end); },
    repeat(arr, n) { return _jac.list.repeat(arr, n); },
    eq(a, b) { return _jac.list.eq(a, b); },
    lt(a, b) { return _jac.list.lt(a, b); },
    gt(a, b) { return _jac.list.gt(a, b); },
    le(a, b) { return _jac.list.le(a, b); },
    ge(a, b) { return _jac.list.ge(a, b); }
  },

  range: {
    count(r, val) {
      if (typeof r[Symbol.iterator] === "function") {
        let c = 0;
        for (const v of r) { if (v === val) c++; }
        return c;
      }
      return 0;
    },
    index(r, val) {
      let i = 0;
      for (const v of r) { if (v === val) return i; i++; }
      throw new _jac.exc.ValueError(val + " is not in range");
    },
    eq(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) { if (a[i] !== b[i]) return false; }
      return true;
    },
    contains(r, val) {
      for (const v of r) { if (v === val) return true; }
      return false;
    }
  },

  builtin: {
    round(n, ndigits) {
      if (ndigits === undefined) return Math.round(n);
      const f = Math.pow(10, ndigits);
      return Math.round(n * f) / f;
    },
    sum(iterable, start) {
      let total = start || 0;
      for (const v of iterable) total += v;
      return total;
    },
    sorted(iterable, key, reverse) {
      const arr = [...iterable];
      if (key) {
        arr.sort((a, b) => {
          const ka = key(a), kb = key(b);
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      } else {
        arr.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
      }
      if (reverse) arr.reverse();
      return arr;
    },
    enumerate(iterable, start) {
      const s = start || 0;
      return [...iterable].map((v, i) => [s + i, v]);
    },
    zip(...iterables) {
      const arrays = iterables.map(it => [...it]);
      const len = Math.min(...arrays.map(a => a.length));
      const result = [];
      for (let i = 0; i < len; i++) {
        result.push(arrays.map(a => a[i]));
      }
      return result;
    },
    map(fn, ...iterables) {
      if (iterables.length === 1) return [...iterables[0]].map(fn);
      const arrays = iterables.map(it => [...it]);
      const len = Math.min(...arrays.map(a => a.length));
      const result = [];
      for (let i = 0; i < len; i++) {
        result.push(fn(...arrays.map(a => a[i])));
      }
      return result;
    },
    filter(fn, iterable) {
      return [...iterable].filter(fn || (x => x));
    },
    // Python subclass relation over builtin-type *tags* -- the string names
    // (`"str"`, `"int"`, ...) that `exit_builtin_type` lowers builtin types to.
    // `object` is the universal base and `bool` is a subclass of `int`; `tuple`
    // and `frozenset` share JS representations with `list`/`set`, so fold those
    // aliases to their runtime tag first. One source of truth so isinstance and
    // issubclass can never disagree.
    _tagSubclass(subTag, superTag) {
      const fold = (t) => t === "tuple" ? "list" : t === "frozenset" ? "set" : t;
      const sub = fold(subTag), sup = fold(superTag);
      if (sup === "object") return true;
      if (sub === sup) return true;
      if (sup === "int" && sub === "bool") return true;
      return false;
    },
    isinstance(o, classinfo) {
      // isinstance(x, (A, B, ...)) -- the tuple of types lowers to a JS array;
      // match if `o` is an instance of any member.
      if (Array.isArray(classinfo)) {
        for (const c of classinfo) {
          if (_jac.builtin.isinstance(o, c)) return true;
        }
        return false;
      }
      // Builtin types lower to a string tag (their Python type name): compare
      // the value's runtime type against it via the shared subclass relation.
      if (typeof classinfo === "string") {
        return _jac.builtin._tagSubclass(_jac.poly._typename(o), classinfo);
      }
      // User archetype / JS constructor.
      if (typeof classinfo === "function") return o instanceof classinfo;
      return false;
    },
    issubclass(cls, classinfo) {
      if (Array.isArray(classinfo)) {
        for (const c of classinfo) {
          if (_jac.builtin.issubclass(cls, c)) return true;
        }
        return false;
      }
      // When either side is a builtin-type tag, compare by the shared subclass
      // relation (covers `object`, `bool` ⊂ `int`, and identity).
      if (typeof cls === "string" || typeof classinfo === "string") {
        return _jac.builtin._tagSubclass(cls, classinfo);
      }
      if (cls === classinfo) return true;
      let proto = cls;
      while (proto) {
        if (proto === classinfo) return true;
        proto = Object.getPrototypeOf(proto);
      }
      return false;
    },
    id(o) {
      if (o === null || o === undefined) return 0;
      if (typeof o === "number") return o;
      return typeof o === "string" ? _jac.builtin._strHash(o) : 0;
    },
    _strHash(s) {
      let h = 0;
      for (let i = 0; i < s.length; i++) {
        h = ((h << 5) - h + s.charCodeAt(i)) | 0;
      }
      return h;
    },
    jid(obj) {
      if (obj === null || obj === undefined) {
        throw new _jac.exc.TypeError(
          "jid() called on " + String(obj)
          + " -- the server may have returned an error instead of a node."
          + " Check that your endpoint returned successfully before using jid()."
        );
      }
      if (obj._jac_id === undefined) {
        throw new _jac.exc.TypeError(
          "jid() expected a node or edge with graph identity, but received "
          + (typeof obj === "object" ? JSON.stringify(Object.keys(obj)) : typeof obj)
          + ". Only node and edge archetypes have a jid."
        );
      }
      return obj._jac_id;
    },
    // unsafe_html(x) returns a sentinel object that the JSX flattener
    // recognises and renders as raw HTML via dangerouslySetInnerHTML.
    // Named "unsafe" so the security implication is visible at the use site.
    unsafe_html(html) {
      return { __jacUnsafeHtml: String(html == null ? "" : html) };
    },
    hash(obj) { return _jac.builtin.id(obj); },
    repr(obj) { return JSON.stringify(obj); },
    pow(base, exp, mod) {
      let result = Math.pow(base, exp);
      if (mod !== undefined) result = ((result % mod) + mod) % mod;
      return result;
    },
    next(iter, default_) {
      const result = iter.next();
      if (result.done) {
        if (default_ !== undefined) return default_;
        throw new _jac.exc.StopIteration("StopIteration");
      }
      return result.value;
    },
    vars(o) {
      if (o === undefined) return {};
      const result = {};
      for (const [k, v] of Object.entries(o)) {
        if (typeof v !== "function") result[k] = v;
      }
      return result;
    },
    dir(o) {
      if (o === undefined) return [];
      return Object.getOwnPropertyNames(o).sort();
    },
    open() { throw new _jac.exc.OSError("open() is not supported in client-side JavaScript"); },
    format(value, format_spec) {
      if (!format_spec) return String(value);
      return String(value);
    },
    ascii(obj) {
      if (typeof obj === "string") {
        let r = "'";
        for (const c of obj) {
          const code = c.charCodeAt(0);
          if (c === "\\") r += "\\\\";
          else if (c === "'") r += "\\'";
          else if (code >= 0x20 && code <= 0x7e) r += c;
          else if (code < 0x100) r += "\\x" + code.toString(16).padStart(2, "0");
          else r += "\\u" + code.toString(16).padStart(4, "0");
        }
        return r + "'";
      }
      return String(obj);
    },
    complex(re, im) { return {re: re || 0, im: im || 0}; },
    bool(x) {
      if (x === null || x === undefined || x === false || x === 0 || x === "" || Number.isNaN(x)) return false;
      if (Array.isArray(x)) return x.length > 0;
      if (x instanceof Set || x instanceof Map) return x.size > 0;
      if (typeof x === "object") return Object.keys(x).length > 0;
      return true;
    },
    range(...args) {
      let start, stop, step;
      if (args.length === 1) { start = 0; stop = args[0]; step = 1; }
      else if (args.length === 2) { start = args[0]; stop = args[1]; step = 1; }
      else { start = args[0]; stop = args[1]; step = args[2]; }
      if (step === 0) throw new _jac.exc.ValueError("range() arg 3 must not be zero");
      const result = [];
      if (step > 0) { for (let i = start; i < stop; i += step) result.push(i); }
      else { for (let i = start; i > stop; i += step) result.push(i); }
      return result;
    },
    slice(...args) {
      if (args.length === 1) return { start: null, stop: args[0], step: null };
      return { start: args[0] !== undefined ? args[0] : null, stop: args[1] !== undefined ? args[1] : null, step: args[2] !== undefined ? args[2] : null };
    },
    format(value, format_spec) {
      if (!format_spec || format_spec === "") return String(value);
      const m = format_spec.match(/^([<>^]?)(\d+)?(?:\.(\d+))?([dfseboxXn%]?)$/);
      if (!m) return String(value);
      const [, align, widthStr, precStr, ftype] = m;
      let s;
      const prec = precStr !== undefined ? parseInt(precStr) : undefined;
      if (ftype === "f" || ftype === "") {
        s = prec !== undefined ? Number(value).toFixed(prec) : String(value);
      } else if (ftype === "d") {
        s = String(Math.floor(Number(value)));
      } else if (ftype === "b") {
        s = Number(value).toString(2);
      } else if (ftype === "o") {
        s = Number(value).toString(8);
      } else if (ftype === "x") {
        s = Number(value).toString(16);
      } else if (ftype === "X") {
        s = Number(value).toString(16).toUpperCase();
      } else if (ftype === "e") {
        s = prec !== undefined ? Number(value).toExponential(prec) : Number(value).toExponential();
      } else if (ftype === "%") {
        s = (prec !== undefined ? (Number(value) * 100).toFixed(prec) : (Number(value) * 100).toFixed(6)) + "%";
      } else if (ftype === "s" || ftype === "n") {
        s = String(value);
      } else {
        s = String(value);
      }
      if (widthStr) {
        const width = parseInt(widthStr);
        if (s.length < width) {
          const pad = " ".repeat(width - s.length);
          if (align === "<") s = s + pad;
          else if (align === "^") {
            const left = Math.floor((width - s.length) / 2);
            s = " ".repeat(left) + s + " ".repeat(width - s.length - left);
          }
          else s = pad + s;
        }
      }
      return s;
    }
  },

  exc: (() => {
    class BaseException extends Error { constructor(m) { super(m); this.name = 'BaseException'; } }
    class Exception extends BaseException { constructor(m) { super(m); this.name = 'Exception'; } }
    class ArithmeticError extends Exception { constructor(m) { super(m); this.name = 'ArithmeticError'; } }
    class ZeroDivisionError extends ArithmeticError { constructor(m) { super(m); this.name = 'ZeroDivisionError'; } }
    class OverflowError extends ArithmeticError { constructor(m) { super(m); this.name = 'OverflowError'; } }
    class FloatingPointError extends ArithmeticError { constructor(m) { super(m); this.name = 'FloatingPointError'; } }
    class LookupError extends Exception { constructor(m) { super(m); this.name = 'LookupError'; } }
    class IndexError extends LookupError { constructor(m) { super(m); this.name = 'IndexError'; } }
    class KeyError extends LookupError { constructor(m) { super(m); this.name = 'KeyError'; } }
    class ValueError extends Exception { constructor(m) { super(m); this.name = 'ValueError'; } }
    class TypeError_ extends Exception { constructor(m) { super(m); this.name = 'TypeError'; } }
    class AttributeError extends Exception { constructor(m) { super(m); this.name = 'AttributeError'; } }
    class RuntimeError extends Exception { constructor(m) { super(m); this.name = 'RuntimeError'; } }
    class NotImplementedError extends RuntimeError { constructor(m) { super(m); this.name = 'NotImplementedError'; } }
    class RecursionError extends RuntimeError { constructor(m) { super(m); this.name = 'RecursionError'; } }
    class OSError extends Exception { constructor(m) { super(m); this.name = 'OSError'; } }
    class FileNotFoundError extends OSError { constructor(m) { super(m); this.name = 'FileNotFoundError'; } }
    class FileExistsError extends OSError { constructor(m) { super(m); this.name = 'FileExistsError'; } }
    class PermissionError extends OSError { constructor(m) { super(m); this.name = 'PermissionError'; } }
    class TimeoutError extends OSError { constructor(m) { super(m); this.name = 'TimeoutError'; } }
    class IsADirectoryError extends OSError { constructor(m) { super(m); this.name = 'IsADirectoryError'; } }
    class NotADirectoryError extends OSError { constructor(m) { super(m); this.name = 'NotADirectoryError'; } }
    class AssertionError extends Exception { constructor(m) { super(m); this.name = 'AssertionError'; } }
    class ImportError extends Exception { constructor(m) { super(m); this.name = 'ImportError'; } }
    class ModuleNotFoundError extends ImportError { constructor(m) { super(m); this.name = 'ModuleNotFoundError'; } }
    class NameError extends Exception { constructor(m) { super(m); this.name = 'NameError'; } }
    class UnboundLocalError extends NameError { constructor(m) { super(m); this.name = 'UnboundLocalError'; } }
    class StopIteration extends Exception { constructor(m) { super(m); this.name = 'StopIteration'; } }
    class StopAsyncIteration extends Exception { constructor(m) { super(m); this.name = 'StopAsyncIteration'; } }
    class EOFError extends Exception { constructor(m) { super(m); this.name = 'EOFError'; } }
    class MemoryError extends Exception { constructor(m) { super(m); this.name = 'MemoryError'; } }
    class ReferenceError_ extends Exception { constructor(m) { super(m); this.name = 'ReferenceError'; } }
    class KeyboardInterrupt extends BaseException { constructor(m) { super(m); this.name = 'KeyboardInterrupt'; } }
    class SystemExit extends BaseException { constructor(m) { super(m); this.name = 'SystemExit'; } }
    class GeneratorExit extends BaseException { constructor(m) { super(m); this.name = 'GeneratorExit'; } }
    return { BaseException, Exception, ArithmeticError, ZeroDivisionError, OverflowError, FloatingPointError, LookupError, IndexError, KeyError, ValueError, TypeError: TypeError_, AttributeError, RuntimeError, NotImplementedError, RecursionError, OSError, FileNotFoundError, FileExistsError, PermissionError, TimeoutError, IsADirectoryError, NotADirectoryError, AssertionError, ImportError, ModuleNotFoundError, NameError, UnboundLocalError, StopIteration, StopAsyncIteration, EOFError, MemoryError, ReferenceError: ReferenceError_, KeyboardInterrupt, SystemExit, GeneratorExit };
  })()
};
function orDays() {
  return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
}
function orTimeLabels() {
  return [{"t": "9a", "f": 0.10}, {"t": "12p", "f": 0.34}, {"t": "3p", "f": 0.58}, {"t": "6p", "f": 0.74}, {"t": "9p", "f": 0.92}];
}
function orFirstWord(name) {
  let _parts = String((name ? name : "")).split(" ");
  if ((_parts.length > 0)) {
    return String(_parts[0]);
  }
  return String((name ? name : ""));
}
function orFitGlyph(fit) {
  if ((fit === "good")) {
    return "\u2713";
  }
  if ((fit === "ok")) {
    return "~";
  }
  return "\u2715";
}
function orPickChipTargets(cands) {
  let _out = [];
  let _winIdx = (-1);
  let _i = 0;
  while ((_i < cands.length)) {
    if (cands.at(_i)["win"]) {
      _winIdx = _i;
      break;
    }
    _i = (_i + 1);
  }
  if ((_winIdx >= 0)) {
    _out = _out.concat([_winIdx]);
  }
  let _badIdx = (-1);
  let _bi = 0;
  while ((_bi < cands.length)) {
    if (((cands.at(_bi)["fit"] === "bad") && (_bi !== _winIdx))) {
      _badIdx = _bi;
      break;
    }
    _bi = (_bi + 1);
  }
  if ((_badIdx >= 0)) {
    _out = _out.concat([_badIdx]);
  }
  let _okIdx = (-1);
  let _oi = 0;
  while ((_oi < cands.length)) {
    if ((((cands.at(_oi)["fit"] === "ok") && (_oi !== _winIdx)) && !_out.includes(_oi))) {
      _okIdx = _oi;
      break;
    }
    _oi = (_oi + 1);
  }
  if ((_okIdx >= 0)) {
    _out = _out.concat([_okIdx]);
  }
  return _out;
}
function OnboardingReveal(props) {
  let _wire = el => {
    if (!el) {
      try {
        if (window.__skoochOrCleanup) {
          window.__skoochOrCleanup();
          window.__skoochOrCleanup = null;
        }
      } catch (__jac_e) {
        if (true) {
          let _orCleanupErr = __jac_e;
        } else {
          throw __jac_e;
        }
      }
      return;
    }
    try {
      if (el.__skoochOrWired) {
        return;
      }
      el.__skoochOrWired = true;
      let _root = el;
      try {
        let _existing = document.getElementById("skooch-onboarding-reveal-css");
        if (!_existing) {
          let _style = document.createElement("style");
          _style.id = "skooch-onboarding-reveal-css";
          _style.textContent = orStyleText();
          document.head.appendChild(_style);
        }
      } catch (__jac_e) {
        if (true) {
          let _ecss = __jac_e;
        } else {
          throw __jac_e;
        }
      }
      let _reduce = false;
      try {
        if (window.matchMedia) {
          _reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        }
      } catch (__jac_e) {
        if (true) {
          let _erm = __jac_e;
          _reduce = false;
        } else {
          throw __jac_e;
        }
      }
      _root.__skoochOrTimers = [];
      _root.__skoochOrGeom = null;
      let _clearTimers = () => {
        try {
          let _ts = _root.__skoochOrTimers;
          let _ti = 0;
          while ((_ti < _ts.length)) {
            clearTimeout(_jac.poly.getitem(_ts, _ti));
            _ti = (_ti + 1);
          }
          _root.__skoochOrTimers = [];
        } catch (__jac_e) {
          if (true) {
            let _ect = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      };
      let _after = (ms, fn) => {
        try {
          let _h = setTimeout(fn, ms);
          _root.__skoochOrTimers = _root.__skoochOrTimers.concat([_h]);
        } catch (__jac_e) {
          if (true) {
            let _eaf = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      };
      let _stage = _root.querySelector(".or-stage");
      let _grid = _root.querySelector(".or-grid");
      let _thinkEl = _root.querySelector(".or-think");
      let _thinkIc = _root.querySelector(".or-think-ic");
      let _thinkTx = _root.querySelector(".or-think-tx");
      let _peakband = _root.querySelector(".or-peakband");
      let _compare = _root.querySelector(".or-compare");
      let _afterPane = _root.querySelector(".or-after-pane");
      let _phandle = _root.querySelector(".or-phandle");
      let _summary = _root.querySelector(".or-summary");
      let _replayBtn = _root.querySelector(".or-replay");
      let _dotsHtml = "<span class=\"or-dots\"><i></i><i></i><i></i></span>";
      let _eShape = "";
      try {
        _eShape = getStoreEnergyShape();
      } catch (__jac_e) {
        if (true) {
          let _ees = __jac_e;
          _eShape = "";
        } else {
          throw __jac_e;
        }
      }
      let _realItems = [];
      try {
        _realItems = revealBuildItems(_eShape);
      } catch (__jac_e) {
        if (true) {
          let _erbi = __jac_e;
          _realItems = [];
        } else {
          throw __jac_e;
        }
      }
      let _realLen = 0;
      try {
        _realLen = (_jac.builtin.bool(_realItems) ? _realItems.length : 0);
      } catch (__jac_e) {
        if (true) {
          let _erl = __jac_e;
          _realLen = 0;
        } else {
          throw __jac_e;
        }
      }
      let _hasReal = (_realLen > 0);
      let _realAfter = [];
      let _realBefore = [];
      let _realCaps = [];
      if (_hasReal) {
        try {
          _realAfter = revealBuildAfter(_realItems);
        } catch (__jac_e) {
          if (true) {
            let _era = __jac_e;
            _realAfter = [];
          } else {
            throw __jac_e;
          }
        }
        try {
          _realBefore = revealBuildBefore(_realItems);
        } catch (__jac_e) {
          if (true) {
            let _erb = __jac_e;
            _realBefore = [];
          } else {
            throw __jac_e;
          }
        }
        try {
          _realCaps = revealBuildCaps(_realItems);
        } catch (__jac_e) {
          if (true) {
            let _erc0 = __jac_e;
            _realCaps = [];
          } else {
            throw __jac_e;
          }
        }
      }
      let _dataItems = () => {
        return (_hasReal ? _realItems : []);
      };
      let _dataAfter = () => {
        return ((_hasReal && (_realAfter.length > 0)) ? _realAfter : []);
      };
      let _dataBefore = () => {
        return ((_hasReal && (_realBefore.length > 0)) ? _realBefore : []);
      };
      let _dataCaps = () => {
        return ((_hasReal && (_realCaps.length > 0)) ? _realCaps : []);
      };
      let _buildGrid = () => {
        try {
          if (!_grid) {
            return;
          }
          _grid.innerHTML = "";
          let _blank = document.createElement("div");
          _grid.appendChild(_blank);
          let _days = orDays();
          let _di = 0;
          while ((_di < _days.length)) {
            let _h = document.createElement("div");
            _h.className = "or-col-head";
            _h.textContent = String(_days.at(_di));
            _grid.appendChild(_h);
            _di = (_di + 1);
          }
          let _tcol = document.createElement("div");
          _tcol.className = "or-time-col";
          let _labs = orTimeLabels();
          let _li = 0;
          while ((_li < _labs.length)) {
            let _l = document.createElement("div");
            _l.className = "or-time-lab";
            _l.style.top = (String((_labs.at(_li)["f"] * 100)) + "%");
            _l.textContent = String(_labs.at(_li)["t"]);
            _tcol.appendChild(_l);
            _li = (_li + 1);
          }
          _grid.appendChild(_tcol);
          let _ci = 0;
          while ((_ci < _days.length)) {
            let _c = document.createElement("div");
            _c.className = "or-daycol";
            _grid.appendChild(_c);
            _ci = (_ci + 1);
          }
        } catch (__jac_e) {
          if (true) {
            let _ebg = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      };
      let _computeGeom = () => {
        try {
          if (!_grid) {
            return;
          }
          let _gw = _grid.clientWidth;
          let _gh = _grid.clientHeight;
          let _padL = 12;
          let _padR = 12;
          let _padT = 10;
          let _padB = 12;
          let _timeColW = 42;
          let _headH = 24;
          let _innerW = ((_gw - _padL) - _padR);
          let _dayW = ((_innerW - _timeColW) / 7);
          let _bandTop = (_padT + _headH);
          let _bandH = (((_gh - _padT) - _padB) - _headH);
          _root.__skoochOrGeom = {"padL": _padL, "timeColW": _timeColW, "dayW": _dayW, "bandTop": _bandTop, "bandH": _bandH};
          _renderPeakBands();
        } catch (__jac_e) {
          if (true) {
            let _ecg = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      };
      let _renderPeakBands = () => {
        try {
          let _g = _root.__skoochOrGeom;
          if (!_g) {
            return;
          }
          let _bt = _g["bandTop"];
          let _bh = _g["bandH"];
          let _extras = _stage.querySelectorAll(".or-peakband.or-extra");
          let _exi = 0;
          while ((_exi < _extras.length)) {
            _jac.poly.call(_jac.poly.getitem(_extras, _exi), "remove");
            _exi = (_exi + 1);
          }
          let _wins = [];
          try {
            _wins = revealPeakWindows(_eShape);
          } catch (__jac_e) {
            if (true) {
              let _erpw = __jac_e;
              _wins = [];
            } else {
              throw __jac_e;
            }
          }
          let _isSteady = false;
          try {
            _isSteady = revealIsSteadyShape(_eShape);
          } catch (__jac_e) {
            if (true) {
              let _eris = __jac_e;
              _isSteady = false;
            } else {
              throw __jac_e;
            }
          }
          if ((_isSteady || (_wins.length === 0))) {
            if (_peakband) {
              _jac.poly.call(_peakband.classList, "add", "or-steady");
              _peakband.style.top = (String((_bt + (0.04 * _bh))) + "px");
              _peakband.style.height = (String((0.90 * _bh)) + "px");
              let _plab0 = _peakband.querySelector(".or-plab");
              if (_plab0) {
                _plab0.textContent = "steady energy \u2014 no single peak";
              }
            }
            return;
          }
          if (_peakband) {
            _jac.poly.call(_peakband.classList, "remove", "or-steady");
            let _w0 = _wins[0];
            _peakband.style.top = (String((_bt + (_w0["top"] * _bh))) + "px");
            _peakband.style.height = (String((_w0["height"] * _bh)) + "px");
            let _plabP = _peakband.querySelector(".or-plab");
            if (_plabP) {
              _plabP.textContent = String(_w0["label"]);
            }
          }
          let _wi = 1;
          while ((_wi < _wins.length)) {
            let _wn = _wins.at(_wi);
            let _eb = document.createElement("div");
            _eb.className = "or-peakband or-extra";
            if ((_peakband && _peakband.classList.contains("show"))) {
              _jac.poly.call(_eb.classList, "add", "show");
            }
            let _ebTop = (_bt + (_wn["top"] * _bh));
            _eb.style.top = (String(_ebTop) + "px");
            _eb.style.height = (String((_wn["height"] * _bh)) + "px");
            let _lab = document.createElement("span");
            _lab.className = "or-plab";
            _lab.textContent = String(_wn["label"]);
            _eb.appendChild(_lab);
            if ((_peakband && _peakband.parentNode)) {
              _peakband.parentNode.insertBefore(_eb, _peakband.nextSibling);
            } else if (_stage) {
              _stage.appendChild(_eb);
            }
            _wi = (_wi + 1);
          }
        } catch (__jac_e) {
          if (true) {
            let _erpb = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      };
      let _showPeak = on => {
        try {
          if (_peakband) {
            if (on) {
              _jac.poly.call(_peakband.classList, "add", "show");
            } else {
              _jac.poly.call(_peakband.classList, "remove", "show");
            }
          }
          let _ebs = _stage.querySelectorAll(".or-peakband.or-extra");
          let _ei = 0;
          while ((_ei < _ebs.length)) {
            if (on) {
              _jac.poly.call(_jac.poly.getitem(_ebs, _ei).classList, "add", "show");
            } else {
              _jac.poly.call(_jac.poly.getitem(_ebs, _ei).classList, "remove", "show");
            }
            _ei = (_ei + 1);
          }
        } catch (__jac_e) {
          if (true) {
            let _esp = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      };
      let _slotX = day => {
        let _g = _root.__skoochOrGeom;
        return (((_g["padL"] + _g["timeColW"]) + (day * _g["dayW"])) + 3);
      };
      let _slotY = top => {
        let _g = _root.__skoochOrGeom;
        return (_g["bandTop"] + (top * _g["bandH"]));
      };
      let _slotW = () => {
        let _g = _root.__skoochOrGeom;
        return (_g["dayW"] - 6);
      };
      let _makeCand = (item, c) => {
        let _e = document.createElement("div");
        _e.className = "or-cand or-neutral";
        _e.innerHTML = (((("<span class=\"or-em\">" + String(item["em"])) + "</span><span class=\"or-lbl\">") + orFirstWord(item["name"])) + "</span><span class=\"or-fit\"></span>");
        _e.style.left = (String(_slotX(c["day"])) + "px");
        _e.style.top = (String(_slotY(c["top"])) + "px");
        _e.style.width = (String(_slotW()) + "px");
        _stage.appendChild(_e);
        return _e;
      };
      let _makeChip = c => {
        let _chip = document.createElement("div");
        _chip.className = ("or-rchip " + String(c["fit"]));
        _chip.innerHTML = (((("<span class=\"or-gl\">" + orFitGlyph(c["fit"])) + "</span><span>") + String(c["reason"])) + "</span>");
        _stage.appendChild(_chip);
        let _cx = _slotX(c["day"]);
        let _cy = _slotY(c["top"]);
        _chip.style.left = "0px";
        _chip.style.top = "0px";
        requestAnimationFrame(() => {
          try {
            let _left = (_cx - 6);
            let _maxLeft = ((_stage.clientWidth - _chip.offsetWidth) - 8);
            if ((_left > _maxLeft)) {
              _left = _maxLeft;
            }
            if ((_left < 8)) {
              _left = 8;
            }
            let _topPos = ((_cy - _chip.offsetHeight) - 7);
            if ((_topPos < 4)) {
              _topPos = (_cy + 26);
            }
            _chip.style.left = (String(_left) + "px");
            _chip.style.top = (String(_topPos) + "px");
            _jac.poly.call(_chip.classList, "add", "show");
          } catch (__jac_e) {
            if (true) {
              let _erc = __jac_e;
            } else {
              throw __jac_e;
            }
          }
        });
        return _chip;
      };
      _root.__skoochOrBlockRects = (_root.__skoochOrBlockRects ? _root.__skoochOrBlockRects : []);
      let _commitBlock = (item, c) => {
        let _b = document.createElement("div");
        _b.className = (("or-block" + (item["t1"] ? " t1" : "")) + " or-settled");
        _b.innerHTML = ((("<span class=\"or-em\">" + String(item["em"])) + "</span>") + orFirstWord(item["name"]));
        let _left = _slotX(c["day"]);
        let _top = _slotY(c["top"]);
        let _blockH = 30;
        let _gap = 4;
        let _step = (_blockH + _gap);
        let _g0 = _root.__skoochOrGeom;
        let _bandBottom = (_g0 ? ((_g0["bandTop"] + _g0["bandH"]) - _blockH) : _top);
        let _rects = _root.__skoochOrBlockRects;
        let _moved = true;
        let _guard = 0;
        while ((_moved && (_guard < 24))) {
          _moved = false;
          let _ri2 = 0;
          while ((_ri2 < _rects.length)) {
            let _rr = _jac.poly.getitem(_rects, _ri2);
            if (((Math.abs((_rr["x"] - _left)) < (_slotW() - 4)) && (Math.abs((_rr["y"] - _top)) < _step))) {
              _top = (_rr["y"] + _step);
              _moved = true;
            }
            _ri2 = (_ri2 + 1);
          }
          _guard = (_guard + 1);
        }
        if ((_g0 && (_top > _bandBottom))) {
          _top = _bandBottom;
        }
        _b.style.left = (String(_left) + "px");
        _b.style.top = (String(_top) + "px");
        _b.style.width = (String(_slotW()) + "px");
        _grid.appendChild(_b);
        _root.__skoochOrBlockRects = _root.__skoochOrBlockRects.concat([{"x": _left, "y": _top}]);
        return _b;
      };
      let _findWin = cands => {
        let _i = 0;
        while ((_i < cands.length)) {
          if (_jac.poly.getitem(cands, _i)["win"]) {
            return _jac.poly.getitem(cands, _i);
          }
          _i = (_i + 1);
        }
        return cands[0];
      };
      let _optimizeItem = (item, startAt) => {
        let _t = startAt;
        let _cands = item["cands"];
        let _holders = [];
        let _hi = 0;
        while ((_hi < _cands.length)) {
          _holders = _holders.concat([{"el": null}]);
          _hi = (_hi + 1);
        }
        _after(_t, () => {
          try {
            if (_thinkEl) {
              _jac.poly.call(_thinkEl.classList, "remove", "show");
            }
            _after((_reduce ? 0 : 160), () => {
              try {
                if (_thinkIc) {
                  _thinkIc.textContent = String(item["em"]);
                }
                if (_thinkEl) {
                  _thinkEl.className = ("or-think show" + (item["warm"] ? " warm" : ""));
                }
                if (_thinkTx) {
                  _thinkTx.innerHTML = ((("Placing <b>" + String(item["name"])) + "</b>") + _dotsHtml);
                }
              } catch (__jac_e) {
                if (true) {
                  let _e1b = __jac_e;
                } else {
                  throw __jac_e;
                }
              }
            });
          } catch (__jac_e) {
            if (true) {
              let _e1 = __jac_e;
            } else {
              throw __jac_e;
            }
          }
        });
        _t = (_t + 440);
        _after(_t, () => {
          try {
            _computeGeom();
            let _gi = 0;
            while ((_gi < _cands.length)) {
              let _idx = _gi;
              _after((_idx * 55), () => {
                try {
                  let _e2 = _makeCand(item, _jac.poly.getitem(_cands, _idx));
                  _holders.at(_idx)["el"] = _e2;
                  requestAnimationFrame(() => {
                    try {
                      _jac.poly.call(_e2.classList, "add", "in");
                    } catch (__jac_e) {
                      if (true) {
                        let _ein2 = __jac_e;
                      } else {
                        throw __jac_e;
                      }
                    }
                  });
                } catch (__jac_e) {
                  if (true) {
                    let _e2c = __jac_e;
                  } else {
                    throw __jac_e;
                  }
                }
              });
              _gi = (_gi + 1);
            }
          } catch (__jac_e) {
            if (true) {
              let _e2 = __jac_e;
            } else {
              throw __jac_e;
            }
          }
        });
        _t = ((_t + (_cands.length * 55)) + 300);
        _after(_t, () => {
          try {
            let _si = 0;
            while ((_si < _cands.length)) {
              let _sidx = _si;
              _after((_sidx * 80), () => {
                try {
                  let _se = _holders.at(_sidx)["el"];
                  if (_se) {
                    _jac.poly.call(_se.classList, "remove", "or-neutral");
                    _jac.poly.call(_se.classList, "add", String(_jac.poly.getitem(_cands, _sidx)["fit"]));
                    _jac.poly.call(_se.classList, "add", "or-scoring");
                  }
                } catch (__jac_e) {
                  if (true) {
                    let _e3b = __jac_e;
                  } else {
                    throw __jac_e;
                  }
                }
              });
              _si = (_si + 1);
            }
          } catch (__jac_e) {
            if (true) {
              let _e3 = __jac_e;
            } else {
              throw __jac_e;
            }
          }
        });
        _t = ((_t + (_cands.length * 80)) + 180);
        let _chipOrder = orPickChipTargets(_cands);
        let _chipEls = [];
        let _co = 0;
        while ((_co < _chipOrder.length)) {
          let _ck = _co;
          _after((_t + (_ck * 300)), () => {
            try {
              let _tgt = _chipOrder.at(_ck);
              if (_holders.at(_tgt)["el"]) {
                _chipEls = _chipEls.concat([_makeChip(_jac.poly.getitem(_cands, _tgt))]);
              }
            } catch (__jac_e) {
              if (true) {
                let _e4b = __jac_e;
              } else {
                throw __jac_e;
              }
            }
          });
          _co = (_co + 1);
        }
        _t = ((_t + (_chipOrder.length * 300)) + 380);
        _after(_t, () => {
          try {
            let _fi = 0;
            while ((_fi < _chipEls.length)) {
              _jac.poly.call(_chipEls.at(_fi).classList, "add", "fade");
              _fi = (_fi + 1);
            }
          } catch (__jac_e) {
            if (true) {
              let _e5 = __jac_e;
            } else {
              throw __jac_e;
            }
          }
        });
        _t = (_t + 260);
        _after(_t, () => {
          try {
            let _ri = 0;
            while ((_ri < _cands.length)) {
              let _rel = _holders.at(_ri)["el"];
              if (((_rel && (_jac.poly.getitem(_cands, _ri)["fit"] === "bad")) && !_jac.poly.getitem(_cands, _ri)["win"])) {
                _jac.poly.call(_rel.classList, "add", "fade");
              }
              _ri = (_ri + 1);
            }
          } catch (__jac_e) {
            if (true) {
              let _e6 = __jac_e;
            } else {
              throw __jac_e;
            }
          }
        });
        _t = (_t + 360);
        _after(_t, () => {
          try {
            let _oi2 = 0;
            while ((_oi2 < _cands.length)) {
              let _oel = _holders.at(_oi2)["el"];
              if (((_oel && (_jac.poly.getitem(_cands, _oi2)["fit"] === "ok")) && !_jac.poly.getitem(_cands, _oi2)["win"])) {
                _jac.poly.call(_oel.classList, "add", "fade");
              }
              _oi2 = (_oi2 + 1);
            }
          } catch (__jac_e) {
            if (true) {
              let _e7 = __jac_e;
            } else {
              throw __jac_e;
            }
          }
        });
        _t = (_t + 340);
        let _winIdx = (-1);
        let _wfi = 0;
        while ((_wfi < _cands.length)) {
          if (_jac.poly.getitem(_cands, _wfi)["win"]) {
            _winIdx = _wfi;
            break;
          }
          _wfi = (_wfi + 1);
        }
        if ((_winIdx < 0)) {
          _winIdx = 0;
        }
        _after(_t, () => {
          try {
            let _wel = _holders.at(_winIdx)["el"];
            if (_wel) {
              _jac.poly.call(_wel.classList, "remove", "good");
              _jac.poly.call(_wel.classList, "remove", "ok");
              _jac.poly.call(_wel.classList, "remove", "bad");
              _jac.poly.call(_wel.classList, "remove", "or-scoring");
              _jac.poly.call(_wel.classList, "add", "or-commit");
              if (item["t1"]) {
                _jac.poly.call(_wel.classList, "add", "t1");
              }
            }
            if (item["winnerIsOk"]) {
              _makeChip({"fit": "ok", "reason": "Best fit this week", "day": _jac.poly.getitem(_cands, _winIdx)["day"], "top": _jac.poly.getitem(_cands, _winIdx)["top"]});
            }
          } catch (__jac_e) {
            if (true) {
              let _e8 = __jac_e;
            } else {
              throw __jac_e;
            }
          }
        });
        _t = (_t + (item["winnerIsOk"] ? 640 : 440));
        _after(_t, () => {
          try {
            let _ci2 = 0;
            while ((_ci2 < _holders.length)) {
              let _cel = _holders.at(_ci2)["el"];
              if (_cel) {
                _jac.poly.call(_cel, "remove");
              }
              _ci2 = (_ci2 + 1);
            }
            let _stray = _stage.querySelectorAll(".or-rchip");
            let _xi = 0;
            while ((_xi < _stray.length)) {
              _jac.poly.call(_jac.poly.getitem(_stray, _xi), "remove");
              _xi = (_xi + 1);
            }
            _commitBlock(item, _findWin(_cands));
          } catch (__jac_e) {
            if (true) {
              let _e9 = __jac_e;
            } else {
              throw __jac_e;
            }
          }
        });
        _t = (_t + 300);
        return (_t - startAt);
      };
      let _quickPlaceItem = (item, startAt) => {
        let _t = startAt;
        _after(_t, () => {
          try {
            if (_thinkIc) {
              _thinkIc.textContent = String(item["em"]);
            }
            if (_thinkEl) {
              _thinkEl.className = ("or-think show" + (item["warm"] ? " warm" : ""));
            }
            if (_thinkTx) {
              _thinkTx.innerHTML = ((("Placing <b>" + String(item["name"])) + "</b>") + _dotsHtml);
            }
            _computeGeom();
            _commitBlock(item, _findWin(item["cands"]));
          } catch (__jac_e) {
            if (true) {
              let _eqp = __jac_e;
            } else {
              throw __jac_e;
            }
          }
        });
        _t = (_t + (_reduce ? 0 : 300));
        return (_t - startAt);
      };
      let _capEls = [];
      let _setSplit = pct => {
        try {
          if (_afterPane) {
            _afterPane.style.clipPath = (("inset(0 0 0 " + String(pct)) + "%)");
          }
          if (_phandle) {
            _phandle.style.left = (String(pct) + "%");
          }
        } catch (__jac_e) {
          if (true) {
            let _ess = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      };
      let _fillPane = (gridSel, items, isAfter) => {
        try {
          let _g = _root.querySelector(gridSel);
          if (!_g) {
            return;
          }
          let _old = _g.querySelectorAll(".or-pchip");
          let _oi3 = 0;
          while ((_oi3 < _old.length)) {
            _jac.poly.call(_jac.poly.getitem(_old, _oi3), "remove");
            _oi3 = (_oi3 + 1);
          }
          if ((_g.querySelectorAll(".or-col-head").length === 0)) {
            let _blankp = document.createElement("div");
            _g.appendChild(_blankp);
            let _days2 = orDays();
            let _dpi = 0;
            while ((_dpi < _days2.length)) {
              let _hp = document.createElement("div");
              _hp.className = "or-col-head";
              _hp.textContent = String(_days2.at(_dpi));
              _g.appendChild(_hp);
              _dpi = (_dpi + 1);
            }
            let _tcolp = document.createElement("div");
            _tcolp.className = "or-time-col";
            let _labs2 = orTimeLabels();
            let _lpi = 0;
            while ((_lpi < _labs2.length)) {
              let _lp = document.createElement("div");
              _lp.className = "or-time-lab";
              _lp.style.top = (String((_labs2.at(_lpi)["f"] * 100)) + "%");
              _lp.textContent = String(_labs2.at(_lpi)["t"]);
              _tcolp.appendChild(_lp);
              _lpi = (_lpi + 1);
            }
            _g.appendChild(_tcolp);
            let _cpi = 0;
            while ((_cpi < _days2.length)) {
              let _cp = document.createElement("div");
              _cp.className = "or-daycol";
              _g.appendChild(_cp);
              _cpi = (_cpi + 1);
            }
          }
          let _gw2 = _g.clientWidth;
          let _gh2 = _g.clientHeight;
          let _padL2 = 12;
          let _padR2 = 12;
          let _padB2 = 12;
          let _padTop2 = 48;
          let _headH2 = 24;
          let _timeColW2 = 42;
          let _innerW2 = ((_gw2 - _padL2) - _padR2);
          let _dayW2 = ((_innerW2 - _timeColW2) / 7);
          let _topBase = (_padTop2 + _headH2);
          let _usableH = ((_gh2 - _padB2) - _topBase);
          let _ii = 0;
          while ((_ii < items.length)) {
            let _it = _jac.poly.getitem(items, _ii);
            let _cc = document.createElement("div");
            let _ccls = "or-pchip";
            if ((_jac.poly.contains(_it, "t1") && _it["t1"])) {
              _ccls = (_ccls + " t1");
            }
            if ((_jac.poly.contains(_it, "cls") && _it["cls"])) {
              _ccls = ((_ccls + " ") + String(_it["cls"]));
            }
            _cc.className = _ccls;
            _cc.innerHTML = ((("<span class=\"or-em\">" + String(_it["em"])) + "</span>") + String(_it["label"]));
            _cc.style.left = (String((((_padL2 + _timeColW2) + (_it["day"] * _dayW2)) + 3)) + "px");
            _cc.style.top = (String((_topBase + (_it["top"] * _usableH))) + "px");
            _cc.style.width = (String((_dayW2 - 6)) + "px");
            if (((isAfter && _jac.poly.contains(_it, "ok")) && _it["ok"])) {
              _cc.style.borderColor = "var(--or-fit-ok-bd)";
              _cc.style.background = "var(--or-fit-ok-bg)";
            }
            _g.appendChild(_cc);
            _ii = (_ii + 1);
          }
          if (isAfter) {
            let _pextras = _g.querySelectorAll(".or-ppeak.or-extra");
            let _pxi = 0;
            while ((_pxi < _pextras.length)) {
              _jac.poly.call(_jac.poly.getitem(_pextras, _pxi), "remove");
              _pxi = (_pxi + 1);
            }
            let _pwins = [];
            try {
              _pwins = revealPeakWindows(_eShape);
            } catch (__jac_e) {
              if (true) {
                let _epw2 = __jac_e;
                _pwins = [];
              } else {
                throw __jac_e;
              }
            }
            let _pSteady = false;
            try {
              _pSteady = revealIsSteadyShape(_eShape);
            } catch (__jac_e) {
              if (true) {
                let _eps2 = __jac_e;
                _pSteady = false;
              } else {
                throw __jac_e;
              }
            }
            let _ppeak = _root.querySelector(".or-ppeak");
            if (_ppeak) {
              let _pplab = _ppeak.querySelector(".or-plab");
              if ((_pSteady || (_pwins.length === 0))) {
                _jac.poly.call(_ppeak.classList, "add", "or-steady");
                _ppeak.style.top = (String((_topBase + (0.04 * _usableH))) + "px");
                _ppeak.style.height = (String((0.90 * _usableH)) + "px");
                if (_pplab) {
                  _pplab.textContent = "steady energy";
                }
              } else {
                _jac.poly.call(_ppeak.classList, "remove", "or-steady");
                let _pw0 = _pwins[0];
                _ppeak.style.top = (String((_topBase + (_pw0["top"] * _usableH))) + "px");
                _ppeak.style.height = (String((_pw0["height"] * _usableH)) + "px");
                if (_pplab) {
                  _pplab.textContent = String(_pw0["label"]);
                }
              }
            }
            let _pwi = 1;
            while ((_pwi < _pwins.length)) {
              let _pwn = _pwins.at(_pwi);
              let _peb = document.createElement("div");
              _peb.className = "or-ppeak or-extra";
              _peb.style.top = (String((_topBase + (_pwn["top"] * _usableH))) + "px");
              _peb.style.height = (String((_pwn["height"] * _usableH)) + "px");
              let _plab2 = document.createElement("span");
              _plab2.className = "or-plab";
              _plab2.textContent = String(_pwn["label"]);
              _peb.appendChild(_plab2);
              _g.appendChild(_peb);
              _pwi = (_pwi + 1);
            }
          }
        } catch (__jac_e) {
          if (true) {
            let _efp = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      };
      let _buildCaps = () => {
        try {
          let _ce = 0;
          while ((_ce < _capEls.length)) {
            _jac.poly.call(_capEls.at(_ce), "remove");
            _ce = (_ce + 1);
          }
          _capEls = [];
          let _ga = _root.querySelector(".or-grid-after");
          if (!_ga) {
            return;
          }
          let _padLc = 12;
          let _timeColWc = 42;
          let _padBc = 12;
          let _padTopc = 48;
          let _headHc = 24;
          let _topBasec = (_padTopc + _headHc);
          let _gauge = () => {
            let _innerWc = (_ga.clientWidth - 24);
            let _dayWc = ((_innerWc - _timeColWc) / 7);
            let _usableHc = ((_ga.clientHeight - _padBc) - _topBasec);
            return {"dayW": _dayWc, "usableH": _usableHc};
          };
          let _cardRects = () => {
            let _rs = [];
            try {
              let _gg = _gauge();
              let _items = _dataAfter();
              let _ii = 0;
              while ((_ii < _items.length)) {
                let _it = _jac.poly.getitem(_items, _ii);
                let _cl = (((_padLc + _timeColWc) + (_it["day"] * _gg["dayW"])) + 3);
                let _ct = (_topBasec + (_it["top"] * _gg["usableH"]));
                _rs = _rs.concat([{"x": _cl, "y": _ct, "w": (_gg["dayW"] - 6), "h": 30}]);
                _ii = (_ii + 1);
              }
            } catch (__jac_e) {
              if (true) {
                let _ecr = __jac_e;
              } else {
                throw __jac_e;
              }
            }
            return _rs;
          };
          let _caps = _dataCaps();
          let _kci = 0;
          while ((_kci < _caps.length)) {
            let _cap = _jac.poly.getitem(_caps, _kci);
            let _ec = document.createElement("div");
            _ec.className = (("or-rchip " + (_cap["warm"] ? "ok" : "good")) + " cap or-callout");
            let _glyphc = (_cap["warm"] ? "~" : "\u2713");
            _ec.innerHTML = (((("<span class=\"or-gl\">" + _glyphc) + "</span><span>") + String(_cap["html"])) + "</span><span class=\"or-callout-stem\"></span>");
            _ec.style.zIndex = "30";
            if (_compare) {
              _compare.appendChild(_ec);
            }
            let _capDay = _cap["day"];
            let _capTop = _cap["top"];
            requestAnimationFrame(() => {
              try {
                let _gg = _gauge();
                let _dayW = _gg["dayW"];
                let _usableH = _gg["usableH"];
                let _cw = _ec.offsetWidth;
                let _ch = _ec.offsetHeight;
                let _stageW = _ga.clientWidth;
                let _cardLeft = (((_padLc + _timeColWc) + (_capDay * _dayW)) + 3);
                let _cardTop = (_topBasec + (_capTop * _usableH));
                let _roomRight = (_stageW - (_cardLeft + _dayW));
                let _roomLeft = (_cardLeft - (_padLc + _timeColWc));
                let _onRight = (_roomRight >= _roomLeft);
                let _gap = 10;
                if (_onRight) {
                  let _left = ((_cardLeft + _dayW) + _gap);
                } else {
                  let _left = ((_cardLeft - _cw) - _gap);
                }
                let _top = ((_cardTop + 15) - (_ch / 2));
                let _maxL = ((_stageW - _cw) - 8);
                if ((_left > _maxL)) {
                  let _left = _maxL;
                }
                if ((_left < 8)) {
                  let _left = 8;
                }
                let _maxT = ((_ga.clientHeight - _ch) - 8);
                let _minT = (_topBasec + 2);
                if ((_top > _maxT)) {
                  _top = _maxT;
                }
                if ((_top < _minT)) {
                  _top = _minT;
                }
                let _rects = _cardRects();
                let _guard2 = 0;
                let _hit = true;
                while ((_hit && (_guard2 < 12))) {
                  _hit = false;
                  let _ri = 0;
                  while ((_ri < _rects.length)) {
                    let _rr = _jac.poly.getitem(_rects, _ri);
                    let _overlapX = ((_left < (_rr["x"] + _rr["w"])) && ((_left + _cw) > _rr["x"]));
                    let _overlapY = ((_top < (_rr["y"] + _rr["h"])) && ((_top + _ch) > _rr["y"]));
                    if ((_overlapX && _overlapY)) {
                      _hit = true;
                      let _down = ((_rr["y"] + _rr["h"]) + 6);
                      if (((_down + _ch) <= _maxT)) {
                        _top = _down;
                      } else {
                        _top = ((_rr["y"] - _ch) - 6);
                      }
                      if ((_top < _minT)) {
                        _top = _minT;
                      }
                    }
                    _ri = (_ri + 1);
                  }
                  _guard2 = (_guard2 + 1);
                }
                _ec.style.left = (String(_left) + "px");
                _ec.style.top = (String(_top) + "px");
              } catch (__jac_e) {
                if (true) {
                  let _erc2 = __jac_e;
                } else {
                  throw __jac_e;
                }
              }
            });
            _capEls = _capEls.concat([_ec]);
            _kci = (_kci + 1);
          }
        } catch (__jac_e) {
          if (true) {
            let _ebc = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      };
      let _runFinale = startAt => {
        let _t = startAt;
        _after(_t, () => {
          try {
            _fillPane(".or-grid-before", _dataBefore(), false);
            _fillPane(".or-grid-after", _dataAfter(), true);
            _buildCaps();
            let _cr = 0;
            while ((_cr < _capEls.length)) {
              _jac.poly.call(_capEls.at(_cr).classList, "remove", "show");
              _cr = (_cr + 1);
            }
            if (_afterPane) {
              _afterPane.style.transition = "none";
            }
            if (_phandle) {
              _phandle.style.transition = "none";
            }
            _setSplit(100);
            if (_compare) {
              _jac.poly.call(_compare.classList, "add", "show");
            }
            if (_thinkEl) {
              _jac.poly.call(_thinkEl.classList, "remove", "show");
            }
          } catch (__jac_e) {
            if (true) {
              let _ef1 = __jac_e;
            } else {
              throw __jac_e;
            }
          }
        });
        _t = (_t + 520);
        _after(_t, () => {
          try {
            if (_compare) {
              let _vw = _compare.offsetWidth;
            }
            if (_reduce) {
              _setSplit(2);
              if ((_capEls.length > 0)) {
                _jac.poly.call(_capEls[0].classList, "add", "show");
              }
            } else {
              if (_afterPane) {
                _afterPane.style.transition = "clip-path 1.7s cubic-bezier(.22,1,.36,1)";
              }
              if (_phandle) {
                _phandle.style.transition = "left 1.7s cubic-bezier(.22,1,.36,1)";
              }
              _setSplit(2);
              let _caps2 = _dataCaps();
              let _dwell = 1500;
              let _xfade = 340;
              let _step = (_dwell + _xfade);
              let _ci3 = 0;
              while ((_ci3 < _caps2.length)) {
                let _cidx = _ci3;
                let _showAt = (800 + (_cidx * _step));
                _after(_showAt, () => {
                  try {
                    if (_capEls.at(_cidx)) {
                      _jac.poly.call(_capEls.at(_cidx).classList, "add", "show");
                    }
                  } catch (__jac_e) {
                    if (true) {
                      let _ecap = __jac_e;
                    } else {
                      throw __jac_e;
                    }
                  }
                });
                if ((_cidx < (_caps2.length - 1))) {
                  _after((_showAt + _dwell), () => {
                    try {
                      if (_capEls.at(_cidx)) {
                        _jac.poly.call(_capEls.at(_cidx).classList, "remove", "show");
                      }
                    } catch (__jac_e) {
                      if (true) {
                        let _ecapf = __jac_e;
                      } else {
                        throw __jac_e;
                      }
                    }
                  });
                }
                _ci3 = (_ci3 + 1);
              }
            }
          } catch (__jac_e) {
            if (true) {
              let _ef2 = __jac_e;
            } else {
              throw __jac_e;
            }
          }
        });
        _t = (_t + (_reduce ? 200 : 1900));
        _enableDrag();
        return (_t - startAt);
      };
      _root.__skoochOrDragging = false;
      let _pctFromX = x => {
        let _r = _compare.getBoundingClientRect();
        let _p = (((x - _r.left) / _r.width) * 100);
        if ((_p < 2)) {
          _p = 2;
        }
        if ((_p > 98)) {
          _p = 98;
        }
        return _p;
      };
      let _dragMove = ev => {
        try {
          if (!_root.__skoochOrDragging) {
            return;
          }
          let _x = ((ev.touches && (ev.touches.length > 0)) ? ev.touches[0].clientX : ev.clientX);
          _setSplit(_pctFromX(_x));
        } catch (__jac_e) {
          if (true) {
            let _edm = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      };
      let _enableDrag = () => {
        try {
          if (!_phandle) {
            return;
          }
          _phandle.onmousedown = ev => {
            _root.__skoochOrDragging = true;
            if (_afterPane) {
              _afterPane.style.transition = "none";
            }
            if (_phandle) {
              _phandle.style.transition = "none";
            }
            _dragMove(ev);
          };
          _phandle.ontouchstart = ev => {
            _root.__skoochOrDragging = true;
            if (_afterPane) {
              _afterPane.style.transition = "none";
            }
            if (_phandle) {
              _phandle.style.transition = "none";
            }
            _dragMove(ev);
          };
        } catch (__jac_e) {
          if (true) {
            let _eed = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      };
      let _mouseUp = ev => {
        _root.__skoochOrDragging = false;
      };
      let _touchEnd = ev => {
        _root.__skoochOrDragging = false;
      };
      try {
        window.addEventListener("mousemove", _dragMove);
        window.addEventListener("touchmove", _dragMove, {"passive": true});
        window.addEventListener("mouseup", _mouseUp);
        window.addEventListener("touchend", _touchEnd);
      } catch (__jac_e) {
        if (true) {
          let _ewl = __jac_e;
        } else {
          throw __jac_e;
        }
      }
      window.__skoochOrCleanup = () => {
        try {
          window.removeEventListener("mousemove", _dragMove);
        } catch (__jac_e) {
          if (true) {
            let _ec1 = __jac_e;
          } else {
            throw __jac_e;
          }
        }
        try {
          window.removeEventListener("touchmove", _dragMove, {"passive": true});
        } catch (__jac_e) {
          if (true) {
            let _ec2 = __jac_e;
          } else {
            throw __jac_e;
          }
        }
        try {
          window.removeEventListener("mouseup", _mouseUp);
        } catch (__jac_e) {
          if (true) {
            let _ec3 = __jac_e;
          } else {
            throw __jac_e;
          }
        }
        try {
          window.removeEventListener("touchend", _touchEnd);
        } catch (__jac_e) {
          if (true) {
            let _ec4 = __jac_e;
          } else {
            throw __jac_e;
          }
        }
        try {
          _clearTimers();
        } catch (__jac_e) {
          if (true) {
            let _ec5 = __jac_e;
          } else {
            throw __jac_e;
          }
        }
        try {
          _root.__skoochOrDragging = false;
        } catch (__jac_e) {
          if (true) {
            let _ec6 = __jac_e;
          } else {
            throw __jac_e;
          }
        }
        try {
          _root.__skoochOrWired = false;
        } catch (__jac_e) {
          if (true) {
            let _ec9 = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      };
      let _resetStage = () => {
        try {
          _clearTimers();
          if (_compare) {
            _jac.poly.call(_compare.classList, "remove", "show");
          }
          if (_summary) {
            _jac.poly.call(_summary.classList, "remove", "show");
          }
          _showPeak(false);
          let _exb = _stage.querySelectorAll(".or-peakband.or-extra, .or-callout");
          let _exbi = 0;
          while ((_exbi < _exb.length)) {
            _jac.poly.call(_jac.poly.getitem(_exb, _exbi), "remove");
            _exbi = (_exbi + 1);
          }
          let _cn = _stage.querySelectorAll(".or-cand, .or-rchip, .or-setup");
          let _cni = 0;
          while ((_cni < _cn.length)) {
            _jac.poly.call(_jac.poly.getitem(_cn, _cni), "remove");
            _cni = (_cni + 1);
          }
          let _bn = _grid.querySelectorAll(".or-block");
          let _bni = 0;
          while ((_bni < _bn.length)) {
            _jac.poly.call(_jac.poly.getitem(_bn, _bni), "remove");
            _bni = (_bni + 1);
          }
          _root.__skoochOrBlockRects = [];
          _capEls = [];
          _buildGrid();
          _computeGeom();
        } catch (__jac_e) {
          if (true) {
            let _ers = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      };
      let _showSetupState = () => {
        try {
          if (_thinkEl) {
            _jac.poly.call(_thinkEl.classList, "remove", "show");
          }
          _showPeak(false);
          if (_compare) {
            _jac.poly.call(_compare.classList, "remove", "show");
          }
          if (_summary) {
            _jac.poly.call(_summary.classList, "remove", "show");
          }
          if (!_stage) {
            return;
          }
          let _existingSetup = _stage.querySelector(".or-setup");
          if (_existingSetup) {
            return;
          }
          let _panel = document.createElement("div");
          _panel.className = "or-setup";
          _panel.innerHTML = "<div class=\"or-setup-ic\">\u2728</div><div class=\"or-setup-title\">Skooch is setting up your plan\u2026</div><div class=\"or-setup-sub\">We'll start scoring your week the moment you add what matters.</div>";
          _stage.appendChild(_panel);
          requestAnimationFrame(() => {
            try {
              _jac.poly.call(_panel.classList, "add", "show");
            } catch (__jac_e) {
              if (true) {
                let _esu = __jac_e;
              } else {
                throw __jac_e;
              }
            }
          });
        } catch (__jac_e) {
          if (true) {
            let _ess2 = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      };
      let _updateSummaryCounts = () => {
        try {
          if (!_summary) {
            return;
          }
          let _items3 = _dataItems();
          let _placed = (_items3 ? _items3.length : 0);
          if ((_placed <= 0)) {
            return;
          }
          let _scored = 0;
          let _si2 = 0;
          while ((_si2 < _items3.length)) {
            let _cds = _jac.poly.getitem(_items3, _si2)["cands"];
            _scored = (_scored + (_cds ? _cds.length : 1));
            _si2 = (_si2 + 1);
          }
          let _stats = _summary.querySelectorAll(".or-stat b");
          if ((_stats && (_stats.length >= 2))) {
            _stats[0].textContent = String(_scored);
            _stats[1].textContent = String(_placed);
          }
          if ((_stats && (_stats.length >= 3))) {
            let _peakLab = "9\u201312";
            try {
              _peakLab = revealPeakStatLabel(_eShape);
            } catch (__jac_e) {
              if (true) {
                let _epl = __jac_e;
                _peakLab = "9\u201312";
              } else {
                throw __jac_e;
              }
            }
            _stats[2].textContent = String(_peakLab);
            let _peakStat = _stats[2].parentNode;
            if (_peakStat) {
              let _peakWord = "peak protected";
              try {
                _peakWord = revealPeakStatWord(_eShape);
              } catch (__jac_e) {
                if (true) {
                  let _epw3 = __jac_e;
                  _peakWord = "peak protected";
                } else {
                  throw __jac_e;
                }
              }
              _peakStat.innerHTML = ((("<b>" + String(_peakLab)) + "</b>") + String(_peakWord));
            }
          }
        } catch (__jac_e) {
          if (true) {
            let _eusc = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      };
      let _run = () => {
        try {
          _resetStage();
          if (!_hasReal) {
            _showSetupState();
            return;
          }
          if (_reduce) {
            _renderPeakBands();
            _showPeak(true);
            if (_thinkIc) {
              _thinkIc.textContent = "\u2728";
            }
            if (_thinkEl) {
              _thinkEl.className = "or-think show";
            }
            if (_thinkTx) {
              _thinkTx.innerHTML = "Skooch is ready for you \u2728";
            }
            let _items = _dataItems();
            let _mi = 0;
            while ((_mi < _items.length)) {
              _commitBlock(_jac.poly.getitem(_items, _mi), _findWin(_jac.poly.getitem(_items, _mi)["cands"]));
              _mi = (_mi + 1);
            }
            _runFinale(300);
            _after(700, () => {
              try {
                _updateSummaryCounts();
                if (_summary) {
                  _jac.poly.call(_summary.classList, "add", "show");
                }
              } catch (__jac_e) {
                if (true) {
                  let _esr = __jac_e;
                } else {
                  throw __jac_e;
                }
              }
            });
            return;
          }
          _after(150, () => {
            try {
              if (_thinkIc) {
                _thinkIc.textContent = "\ud83d\udcad";
              }
              if (_thinkEl) {
                _thinkEl.className = "or-think show";
              }
              if (_thinkTx) {
                _thinkTx.innerHTML = ("Skooch is learning how you work" + _dotsHtml);
              }
            } catch (__jac_e) {
              if (true) {
                let _ein3 = __jac_e;
              } else {
                throw __jac_e;
              }
            }
          });
          _after(700, () => {
            try {
              _showPeak(true);
            } catch (__jac_e) {
              if (true) {
                let _epb = __jac_e;
              } else {
                throw __jac_e;
              }
            }
          });
          let _strategicCount = 3;
          let _t = 900;
          let _speeds = [0.92, 0.82, 0.74, 0.72];
          let _items2 = _dataItems();
          let _ai = 0;
          while ((_ai < _items2.length)) {
            if ((_ai < _strategicCount)) {
              let _consumed = _optimizeItem(_jac.poly.getitem(_items2, _ai), _t);
              let _spd = ((_ai < _speeds.length) ? _speeds.at(_ai) : 0.72);
              _t = ((_t + (_consumed * _spd)) + 200);
            } else {
              let _consumedQ = _quickPlaceItem(_jac.poly.getitem(_items2, _ai), _t);
              _t = ((_t + _consumedQ) + 90);
            }
            _ai = (_ai + 1);
          }
          _after(_t, () => {
            try {
              if (_thinkIc) {
                _thinkIc.textContent = "\u2728";
              }
              if (_thinkEl) {
                _thinkEl.className = "or-think show";
              }
              if (_thinkTx) {
                _thinkTx.innerHTML = "Skooch is ready for you \u2728";
              }
            } catch (__jac_e) {
              if (true) {
                let _efin = __jac_e;
              } else {
                throw __jac_e;
              }
            }
          });
          _t = (_t + 700);
          _runFinale(_t);
          _t = (_t + 2600);
          _after(_t, () => {
            try {
              _updateSummaryCounts();
              if (_summary) {
                _jac.poly.call(_summary.classList, "add", "show");
              }
            } catch (__jac_e) {
              if (true) {
                let _esum = __jac_e;
              } else {
                throw __jac_e;
              }
            }
          });
        } catch (__jac_e) {
          if (true) {
            let _erun = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      };
      if (_replayBtn) {
        _replayBtn.addEventListener("click", () => {
          _run();
        });
      }
      _buildGrid();
      _computeGeom();
      _after(300, () => {
        _run();
      });
    } catch (__jac_e) {
      if (true) {
        let _eWire = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
  return __jacJsx("div", {"className": "onboarding-reveal-root", "ref": _wire}, [__jacJsx("div", {"className": "or-head"}, [__jacJsx("div", {"className": "or-head-text"}, [__jacJsx("div", {"className": "or-eyebrow"}, ["Your week \u00b7 scored slot by slot"]), __jacJsx("h2", {"className": "or-title"}, ["Watch Skooch think it through"]), __jacJsx("p", {"className": "or-lede"}, ["For each thing that matters, Skooch tries the open slots, scores how well each one fits your energy and priorities, and keeps the best \u2014 honest when it has to settle."])]), __jacJsx("button", {"type": "button", "className": "or-replay"}, ["\u21bb Replay"])]), __jacJsx("div", {"className": "or-thinkbar"}, [__jacJsx("div", {"className": "or-think"}, [__jacJsx("div", {"className": "or-think-ic"}, ["\ud83d\udcad"]), __jacJsx("div", {"className": "or-think-tx"}, ["Reading your priorities"]), __jacJsx("div", {"className": "or-legend"}, [__jacJsx("span", {"className": "or-lg"}, [__jacJsx("span", {"className": "or-dot good"}, ["\u2713"]), "belongs here"]), __jacJsx("span", {"className": "or-lg"}, [__jacJsx("span", {"className": "or-dot ok"}, ["~"]), "would settle"]), __jacJsx("span", {"className": "or-lg"}, [__jacJsx("span", {"className": "or-dot bad"}, ["\u2715"]), "wrong slot"])])])]), __jacJsx("div", {"className": "or-stage"}, [__jacJsx("div", {"className": "or-grid"}, []), __jacJsx("div", {"className": "or-peakband"}, [__jacJsx("span", {"className": "or-plab"}, ["your peak"])]), __jacJsx("div", {"className": "or-compare"}, [__jacJsx("div", {"className": "or-pane before"}, [__jacJsx("div", {"className": "or-pbanner"}, [__jacJsx("span", {}, ["\ud83d\ude35\u200d\ud83d\udcab A normal calendar"]), __jacJsx("span", {"className": "or-ptag"}, ["guessed"])]), __jacJsx("div", {"className": "or-pgrid or-grid-before"}, [])]), __jacJsx("div", {"className": "or-pane after or-after-pane"}, [__jacJsx("div", {"className": "or-pbanner"}, [__jacJsx("span", {}, ["\u2728 Your week, optimized"]), __jacJsx("span", {"className": "or-ptag"}, ["scored for you"])]), __jacJsx("div", {"className": "or-pgrid or-grid-after"}, [__jacJsx("div", {"className": "or-ppeak"}, [__jacJsx("span", {"className": "or-plab"}, ["your peak"])])])]), __jacJsx("div", {"className": "or-phandle"}, [__jacJsx("div", {"className": "or-grip"}, ["\u27e8\u27e9"])])])]), __jacJsx("div", {"className": "or-foot"}, [__jacJsx("div", {"className": "or-summary"}, [__jacJsx("span", {"className": "or-stat"}, [__jacJsx("b", {}, ["11"]), "slots scored"]), __jacJsx("span", {"className": "or-stat"}, [__jacJsx("b", {}, ["4"]), "placed for you"]), __jacJsx("span", {"className": "or-stat"}, [__jacJsx("b", {}, ["peak"]), "peak protected"])])])]);
}
function orStyleText() {
  return (((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((".onboarding-reveal-root{--or-cream:#fffefa;--or-bg-primary:#f6f4ec;--or-bg-secondary:#efeadf;--or-sage:#5F7A52;--or-sage-hover:#4c6442;--or-sage-light:rgba(95,122,82,0.12);--or-amber:#C98A1F;--or-amber-light:rgba(201,138,31,0.14);--or-ink:#322f27;--or-ink-soft:#716d5f;--or-ink-muted:#a8a18c;--or-border:#e3ddcf;--or-shadow-sm:0 1px 2px rgba(45,40,25,0.05);--or-shadow-md:0 4px 14px rgba(45,40,25,0.08);--or-shadow-lg:0 10px 30px rgba(45,40,25,0.10);--or-radius-lg:18px;--or-radius-xl:24px;--or-fit-good:#5F7A52;--or-fit-good-bg:#e9f0e2;--or-fit-good-bd:#a9c197;--or-fit-ok:#B07A12;--or-fit-ok-bg:#fbf1da;--or-fit-ok-bd:#e3c585;--or-fit-bad:#B0533A;--or-fit-bad-bg:#f7e7e0;--or-fit-bad-bd:#dba892;color:var(--or-ink)}" + ".onboarding-reveal-root *,.onboarding-reveal-root *::before,.onboarding-reveal-root *::after{box-sizing:border-box;margin:0;padding:0}") + ".onboarding-reveal-root .or-head{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;margin-bottom:6px}") + ".onboarding-reveal-root .or-eyebrow{font-size:.72rem;letter-spacing:.16em;text-transform:uppercase;color:var(--or-sage);font-weight:700;margin-bottom:8px}") + ".onboarding-reveal-root .or-title{font-family:'Hedvig Letters Serif',Georgia,serif;font-size:1.5rem;font-weight:500;letter-spacing:-0.01em;margin-bottom:5px;color:var(--or-ink)}") + ".onboarding-reveal-root .or-lede{color:var(--or-ink-soft);font-size:.94rem;line-height:1.5;max-width:560px}") + ".onboarding-reveal-root .or-replay{flex-shrink:0;background:transparent;color:var(--or-sage-hover);border:1px solid var(--or-border);font-family:inherit;font-weight:600;font-size:.82rem;padding:8px 15px;border-radius:999px;cursor:pointer;transition:background .15s,border-color .15s}") + ".onboarding-reveal-root .or-replay:hover{background:var(--or-sage-light);border-color:#c4d3b6}") + ".onboarding-reveal-root .or-thinkbar{margin-top:14px;height:46px;position:relative}") + ".onboarding-reveal-root .or-think{position:absolute;inset:0;display:flex;align-items:center;gap:12px;background:var(--or-cream);border:1px solid var(--or-border);border-radius:14px;padding:9px 16px;box-shadow:var(--or-shadow-md);opacity:0;transform:translateY(8px) scale(.99);transition:opacity .4s ease,transform .45s cubic-bezier(.22,1,.36,1)}") + ".onboarding-reveal-root .or-think.show{opacity:1;transform:none}") + ".onboarding-reveal-root .or-think-ic{flex-shrink:0;width:30px;height:30px;border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:1.05rem;background:var(--or-sage-light)}") + ".onboarding-reveal-root .or-think.warm .or-think-ic{background:var(--or-amber-light)}") + ".onboarding-reveal-root .or-think-tx{font-size:.9rem;font-weight:600;color:var(--or-ink)}") + ".onboarding-reveal-root .or-think-tx b{color:var(--or-sage-hover);font-weight:700}") + ".onboarding-reveal-root .or-think.warm .or-think-tx b{color:var(--or-amber)}") + ".onboarding-reveal-root .or-dots{display:inline-flex;gap:3px;margin-left:6px;vertical-align:middle}") + ".onboarding-reveal-root .or-dots i{width:4px;height:4px;border-radius:50%;background:var(--or-sage);opacity:.35;animation:or-blink 1.1s infinite}") + ".onboarding-reveal-root .or-think.warm .or-dots i{background:var(--or-amber)}") + ".onboarding-reveal-root .or-dots i:nth-child(2){animation-delay:.18s}") + ".onboarding-reveal-root .or-dots i:nth-child(3){animation-delay:.36s}") + "@keyframes or-blink{0%,60%,100%{opacity:.3}30%{opacity:1}}") + ".onboarding-reveal-root .or-legend{margin-left:auto;display:flex;gap:12px;align-items:center}") + ".onboarding-reveal-root .or-lg{display:inline-flex;align-items:center;gap:5px;font-size:.7rem;font-weight:600;color:var(--or-ink-soft)}") + ".onboarding-reveal-root .or-legend .or-dot{width:14px;height:14px;border-radius:5px;display:inline-flex;align-items:center;justify-content:center;font-size:.62rem;font-weight:800;line-height:1}") + ".onboarding-reveal-root .or-dot.good{background:var(--or-fit-good-bg);color:var(--or-fit-good);border:1px solid var(--or-fit-good-bd)}") + ".onboarding-reveal-root .or-dot.ok{background:var(--or-fit-ok-bg);color:var(--or-fit-ok);border:1px solid var(--or-fit-ok-bd)}") + ".onboarding-reveal-root .or-dot.bad{background:var(--or-fit-bad-bg);color:var(--or-fit-bad);border:1px solid var(--or-fit-bad-bd)}") + "@media(max-width:720px){.onboarding-reveal-root .or-legend{display:none}}") + ".onboarding-reveal-root .or-stage{position:relative;height:420px;margin-top:14px;border-radius:var(--or-radius-lg);background:linear-gradient(180deg,#f3f0e6,#efeadf);border:1px solid var(--or-border);overflow:hidden}") + ".onboarding-reveal-root .or-grid{position:absolute;inset:0;display:grid;grid-template-columns:42px repeat(7,1fr);grid-template-rows:24px 1fr;padding:10px 12px 12px}") + ".onboarding-reveal-root .or-col-head{font-size:.64rem;font-weight:700;color:var(--or-ink-muted);text-align:center;letter-spacing:.04em;text-transform:uppercase;align-self:end;padding-bottom:4px}") + ".onboarding-reveal-root .or-time-col{position:relative}") + ".onboarding-reveal-root .or-time-lab{position:absolute;right:6px;font-size:.58rem;color:var(--or-ink-muted);font-weight:600;transform:translateY(-50%)}") + ".onboarding-reveal-root .or-daycol{position:relative;border-left:1px solid rgba(227,221,207,0.65)}") + ".onboarding-reveal-root .or-daycol:last-child{border-right:1px solid rgba(227,221,207,0.65)}") + ".onboarding-reveal-root .or-peakband{position:absolute;left:42px;right:12px;background:var(--or-amber-light);border-top:1px dashed rgba(201,138,31,0.45);border-bottom:1px dashed rgba(201,138,31,0.45);opacity:0;transition:opacity .5s ease}") + ".onboarding-reveal-root .or-peakband.show{opacity:1}") + ".onboarding-reveal-root .or-peakband.or-steady{background:linear-gradient(180deg,rgba(95,122,82,0.05),rgba(95,122,82,0.09),rgba(95,122,82,0.05));border-top:none;border-bottom:none}") + ".onboarding-reveal-root .or-peakband.or-steady .or-plab{color:var(--or-sage-hover);left:50%;transform:translateX(-50%);text-align:center}") + ".onboarding-reveal-root .or-plab{position:absolute;left:8px;top:3px;font-size:.6rem;font-weight:700;color:var(--or-amber);letter-spacing:.03em;text-transform:uppercase}") + ".onboarding-reveal-root .or-block{position:absolute;display:flex;align-items:center;gap:5px;font-size:.72rem;font-weight:600;color:var(--or-ink);background:var(--or-cream);border:1px solid var(--or-border);border-radius:9px;padding:5px 8px;box-shadow:var(--or-shadow-sm);white-space:nowrap;z-index:3}") + ".onboarding-reveal-root .or-block .or-em{font-size:.86rem;line-height:1}") + ".onboarding-reveal-root .or-block.t1{border-color:#c4d3b6;background:#eef3e8}") + ".onboarding-reveal-root .or-block.or-settled{animation:or-settle .42s cubic-bezier(.34,1.56,.64,1)}") + "@keyframes or-settle{0%{transform:scale(1.18);box-shadow:0 0 0 4px rgba(95,122,82,.16),var(--or-shadow-md)}55%{transform:scale(.97)}100%{transform:scale(1);box-shadow:var(--or-shadow-sm)}}") + ".onboarding-reveal-root .or-cand{position:absolute;display:flex;align-items:center;gap:4px;font-size:.7rem;font-weight:600;border-radius:9px;padding:4px 7px;white-space:nowrap;z-index:4;opacity:0;transform:scale(.82);transition:opacity .3s ease,transform .35s cubic-bezier(.22,1,.36,1),left .55s cubic-bezier(.22,1,.36,1),top .55s cubic-bezier(.22,1,.36,1),background .3s,border-color .3s,color .3s}") + ".onboarding-reveal-root .or-cand .or-em{font-size:.82rem;line-height:1}") + ".onboarding-reveal-root .or-cand .or-fit{margin-left:1px;font-size:.66rem;font-weight:800;width:13px;text-align:center}") + ".onboarding-reveal-root .or-cand.in{opacity:.92;transform:scale(1)}") + ".onboarding-reveal-root .or-cand.or-scoring{border-style:dashed}") + ".onboarding-reveal-root .or-cand.good{background:var(--or-fit-good-bg);border:1px solid var(--or-fit-good-bd);color:var(--or-fit-good)}") + ".onboarding-reveal-root .or-cand.good .or-fit::before{content:'\\2713'}") + ".onboarding-reveal-root .or-cand.ok{background:var(--or-fit-ok-bg);border:1px solid var(--or-fit-ok-bd);color:var(--or-fit-ok)}") + ".onboarding-reveal-root .or-cand.ok .or-fit::before{content:'\\223C'}") + ".onboarding-reveal-root .or-cand.bad{background:var(--or-fit-bad-bg);border:1px solid var(--or-fit-bad-bd);color:var(--or-fit-bad)}") + ".onboarding-reveal-root .or-cand.bad .or-fit::before{content:'\\2715'}") + ".onboarding-reveal-root .or-cand.or-neutral{background:rgba(255,254,250,.78);border:1px dashed var(--or-border);color:var(--or-ink-muted)}") + ".onboarding-reveal-root .or-cand.fade{opacity:0;transform:scale(.7) translateY(4px);transition:opacity .45s ease,transform .45s ease}") + ".onboarding-reveal-root .or-cand.or-commit{opacity:1;transform:scale(1);background:var(--or-cream);border:1px solid var(--or-border);color:var(--or-ink);z-index:6}") + ".onboarding-reveal-root .or-cand.or-commit.t1{border-color:#c4d3b6;background:#eef3e8}") + ".onboarding-reveal-root .or-cand.or-commit .or-fit{display:none}") + ".onboarding-reveal-root .or-rchip{position:absolute;display:flex;gap:6px;align-items:center;background:rgba(255,254,250,0.98);border:1px solid var(--or-border);border-left:3px solid var(--or-sage);border-radius:10px;padding:5px 10px;font-size:.72rem;font-weight:600;color:var(--or-ink);box-shadow:var(--or-shadow-md);opacity:0;transform:translateY(6px) scale(.97);transition:opacity .35s ease,transform .4s cubic-bezier(.22,1,.36,1);z-index:8;pointer-events:none;white-space:nowrap}") + ".onboarding-reveal-root .or-rchip.show{opacity:1;transform:none}") + ".onboarding-reveal-root .or-rchip .or-gl{font-size:.78rem;font-weight:800;line-height:1}") + ".onboarding-reveal-root .or-rchip.good{border-left-color:var(--or-fit-good)}.onboarding-reveal-root .or-rchip.good .or-gl{color:var(--or-fit-good)}") + ".onboarding-reveal-root .or-rchip.ok{border-left-color:var(--or-fit-ok)}.onboarding-reveal-root .or-rchip.ok .or-gl{color:var(--or-fit-ok)}") + ".onboarding-reveal-root .or-rchip.bad{border-left-color:var(--or-fit-bad)}.onboarding-reveal-root .or-rchip.bad .or-gl{color:var(--or-fit-bad)}") + ".onboarding-reveal-root .or-rchip.fade{opacity:0;transform:scale(.85) translateY(4px)}") + ".onboarding-reveal-root .or-compare{position:absolute;inset:0;z-index:20;opacity:0;pointer-events:none;transition:opacity .5s ease}") + ".onboarding-reveal-root .or-compare.show{opacity:1;pointer-events:auto}") + ".onboarding-reveal-root .or-pane{position:absolute;inset:0}") + ".onboarding-reveal-root .or-pgrid{position:absolute;inset:0;display:grid;grid-template-columns:42px repeat(7,1fr);grid-template-rows:24px 1fr;padding:48px 12px 12px}") + ".onboarding-reveal-root .or-pbanner{position:absolute;top:0;left:0;right:0;z-index:4;display:flex;align-items:center;gap:9px;padding:13px 16px 9px;font-weight:700;font-size:.78rem;letter-spacing:.04em;text-transform:uppercase}") + ".onboarding-reveal-root .or-pane.before{background:#f1ece0;z-index:21}") + ".onboarding-reveal-root .or-pane.before .or-pbanner{color:var(--or-fit-bad);background:#f1ece0}") + ".onboarding-reveal-root .or-pane.after{background:linear-gradient(180deg,#f4f2e8,#eef2e8);z-index:22}") + ".onboarding-reveal-root .or-pane.after .or-pbanner{color:var(--or-sage-hover);background:#f1f4ea}") + ".onboarding-reveal-root .or-ptag{font-size:.58rem;font-weight:700;padding:3px 9px;border-radius:999px}") + ".onboarding-reveal-root .or-pane.before .or-ptag{background:var(--or-fit-bad-bg);color:var(--or-fit-bad)}") + ".onboarding-reveal-root .or-pane.after .or-ptag{background:var(--or-sage-light);color:var(--or-sage-hover)}") + ".onboarding-reveal-root .or-pane .or-col-head{padding-bottom:5px}") + ".onboarding-reveal-root .or-ppeak{position:absolute;left:42px;right:12px;background:var(--or-amber-light);border-top:1px dashed rgba(201,138,31,0.45);border-bottom:1px dashed rgba(201,138,31,0.45)}") + ".onboarding-reveal-root .or-ppeak.or-steady{background:linear-gradient(180deg,rgba(95,122,82,0.05),rgba(95,122,82,0.09),rgba(95,122,82,0.05));border-top:none;border-bottom:none}") + ".onboarding-reveal-root .or-ppeak.or-steady .or-plab{color:var(--or-sage-hover);left:50%;transform:translateX(-50%);text-align:center}") + ".onboarding-reveal-root .or-callout{max-width:210px;white-space:normal;line-height:1.3}") + ".onboarding-reveal-root .or-callout .or-callout-stem{display:none}") + ".onboarding-reveal-root .or-pchip{position:absolute;display:flex;align-items:center;gap:5px;font-size:.7rem;font-weight:600;color:var(--or-ink);background:var(--or-cream);border:1px solid var(--or-border);border-radius:9px;padding:5px 7px;box-shadow:var(--or-shadow-sm);white-space:nowrap}") + ".onboarding-reveal-root .or-pchip .or-em{font-size:.84rem;line-height:1}") + ".onboarding-reveal-root .or-pchip.t1{border-color:#c4d3b6;background:#eef3e8}") + ".onboarding-reveal-root .or-pchip.bad{border:1px solid var(--or-fit-bad-bd);background:var(--or-fit-bad-bg);transform:rotate(-1.5deg)}") + ".onboarding-reveal-root .or-pchip.bad.b2{transform:rotate(2deg)}") + ".onboarding-reveal-root .or-pchip.miss{border-style:dashed;color:var(--or-fit-bad);background:var(--or-fit-bad-bg);border-color:var(--or-fit-bad-bd)}") + ".onboarding-reveal-root .or-pane.after{clip-path:inset(0 0 0 100%);transition:clip-path 1.7s cubic-bezier(.22,1,.36,1)}") + ".onboarding-reveal-root .or-phandle{position:absolute;top:0;bottom:0;left:100%;width:3px;background:var(--or-sage);box-shadow:0 0 0 1px rgba(255,254,250,.7);z-index:26;transition:left 1.7s cubic-bezier(.22,1,.36,1)}") + ".onboarding-reveal-root .or-grip{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:34px;height:34px;border-radius:50%;background:var(--or-sage);color:#fff;display:flex;align-items:center;justify-content:center;font-size:.86rem;font-weight:700;box-shadow:var(--or-shadow-md);cursor:ew-resize}") + ".onboarding-reveal-root .or-foot{margin-top:16px;display:flex;align-items:center;justify-content:center;gap:14px;flex-wrap:wrap;min-height:30px}") + ".onboarding-reveal-root .or-summary{display:flex;gap:18px;font-size:.84rem;color:var(--or-ink-soft);opacity:0;transition:opacity .5s ease}") + ".onboarding-reveal-root .or-summary.show{opacity:1}") + ".onboarding-reveal-root .or-stat b{font-family:'Hedvig Letters Serif',Georgia,serif;font-size:1.15rem;color:var(--or-sage-hover);font-weight:500;margin-right:5px}") + ".onboarding-reveal-root .or-setup{position:absolute;inset:0;z-index:25;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;gap:10px;padding:24px;opacity:0;transform:translateY(6px);transition:opacity .5s ease,transform .5s cubic-bezier(.22,1,.36,1)}") + ".onboarding-reveal-root .or-setup.show{opacity:1;transform:none}") + ".onboarding-reveal-root .or-setup-ic{width:46px;height:46px;border-radius:13px;display:flex;align-items:center;justify-content:center;font-size:1.4rem;background:var(--or-sage-light);animation:or-setup-pulse 2.2s ease-in-out infinite}") + "@keyframes or-setup-pulse{0%,100%{transform:scale(1);opacity:.85}50%{transform:scale(1.06);opacity:1}}") + ".onboarding-reveal-root .or-setup-title{font-family:'Hedvig Letters Serif',Georgia,serif;font-size:1.18rem;font-weight:500;color:var(--or-ink)}") + ".onboarding-reveal-root .or-setup-sub{font-size:.9rem;color:var(--or-ink-soft);max-width:340px;line-height:1.5}") + "@media (max-width:680px){.onboarding-reveal-root .or-title{font-size:1.3rem}.onboarding-reveal-root .or-stage{height:360px}.onboarding-reveal-root .or-summary{gap:12px;font-size:.78rem}.onboarding-reveal-root .or-setup-title{font-size:1.05rem}}") + "@media (prefers-reduced-motion: reduce){.onboarding-reveal-root .or-think,.onboarding-reveal-root .or-cand,.onboarding-reveal-root .or-rchip,.onboarding-reveal-root .or-block,.onboarding-reveal-root .or-compare,.onboarding-reveal-root .or-pane.after,.onboarding-reveal-root .or-phandle,.onboarding-reveal-root .or-summary,.onboarding-reveal-root .or-setup,.onboarding-reveal-root .or-setup-ic{transition:none!important;animation:none!important}}");
}
export {OnboardingReveal, orDays, orFirstWord, orFitGlyph, orPickChipTargets, orStyleText, orTimeLabels};
if (typeof globalThis !== "undefined") { if (!globalThis.__jacEndpointEffects__) globalThis.__jacEndpointEffects__ = {}; Object.assign(globalThis.__jacEndpointEffects__, {"func:orDays": {"is_writer": false, "touches": ["*"]}, "func:orTimeLabels": {"is_writer": false, "touches": ["*"]}, "func:orFirstWord": {"is_writer": false, "touches": ["*"]}, "func:orFitGlyph": {"is_writer": false, "touches": ["*"]}, "func:orPickChipTargets": {"is_writer": false, "touches": ["*"]}, "func:OnboardingReveal": {"is_writer": true, "touches": ["*"]}, "func:orStyleText": {"is_writer": false, "touches": ["*"]}}); };
//# sourceMappingURL=onboarding_reveal.js.map
